import { useState, useEffect, useRef, useCallback, useMemo, Component, type ReactNode, type ErrorInfo } from 'react'
import { createPortal } from 'react-dom'
import { FileText, Film, Loader2, Download, Check, Clock, AlertCircle, AlertTriangle, X, Palette, Upload, FileEdit, Video, Play, ExternalLink, Sparkles, RefreshCw, RotateCcw, ArrowRight, ArrowUpFromLine, Layers, Mic, ImageIcon, UserRound, Gauge, Diamond, ChevronDown, Captions, ScrollText } from 'lucide-react'
import { downloadVideo } from '@/lib/download'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { useMutation, useQuery } from 'convex/react'
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle, type GroupImperativeHandle } from 'react-resizable-panels'
import { RightSidebar } from './RightSidebar'
import { VideoPreview } from './VideoPreview'
import { VersionSelector } from './VersionSelector'
import { NarrativeEditor } from './NarrativeEditor'
import { VoicePicker } from './VoicePicker'
import { Timeline, getTimelineHeight } from './Timeline'
import { useVideoSeekOptional } from '@/contexts/video-seek-context'
// Audio tracks are now integrated directly into VideoPreview
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { cn } from '@/lib/utils'
import { track } from '@/lib/track'
import { Lightbox } from '@/components/ui/lightbox'
import { useFF } from '@/platform/feature-flags'
import { toast } from '@/hooks/use-toast'
import { resolveDisplayClipMode, hasClipContinuity } from './clipMode'
import { getEffectiveDuration, buildTimelineModel, isImageOnlyClip, type TimelineClip, type TextOverlay } from '@/lib/clipUtils'
import { TransitionIndicator } from './TransitionPicker'
import { useProjectStore } from '@/lib/projectStore'
import { matchPreset as matchMotionPreset } from '@/lib/motionPresets'
import { AudioUploadHero } from './AudioUploadHero'
import { ScriptPanel } from './script-editor/ScriptPanel'
import type { UiLayoutState } from '@/hooks/useUiLayout'
import { ExportFeedbackModal, useExportFeedbackGate } from './ExportFeedbackModal'

interface CharacterReferenceImage {
  url: string
  pose: string
  generatedAt?: number
}

interface WorkflowCharacter {
  id: string
  name: string
  description: string
  referenceImages?: CharacterReferenceImage[]
  referenceStatus?: 'pending' | 'generating' | 'completed' | 'failed'
  voiceId?: string
  voiceProvider?: string
  voiceSampleUrl?: string
  klingVoiceId?: string
  klingVoiceStatus?: 'none' | 'creating' | 'ready' | 'failed'
}

interface ToolCall {
  id: string
  name: string
  input?: Record<string, unknown> | null
  status?: 'running' | 'completed' | 'failed'
  result?: {
    success?: boolean
    data?: unknown
    error?: string
    input?: unknown
  }
}

interface ToolResult {
  toolCallId: string
  result: unknown
}

interface WorkflowMessage {
  _id?: string
  id?: string
  role: string
  content: string
  toolCalls?: ToolCall[]
  toolResults?: ToolResult[]
  isStreaming?: boolean
  createdAt?: number
  timestamp?: number
}

interface Keyframe {
  url: string
  timestamp: number // 0 = start, 0.5 = mid, 1 = end
  description: string
  status?: 'pending' | 'generating' | 'ready' | 'failed'
}

interface WorkflowClip {
  _id: string
  title?: string
  description?: string
  videoPrompt?: string
  visualDescription?: string // Legacy field - fall back to this if videoPrompt not set
  promptSummary?: string
  charactersInClip?: string[] // Character IDs that appear in this clip
  isJumpCut?: boolean // True = don't use previous clip's reference frame
  generationMode?: 'text-to-video' | 'image-to-video' | 'reference-to-video' | 'first-last-frame' | 'edit-video' | 'video-reference' | 'avatar' | 'multi-shot' | 'uploaded' | 'extend-video'
  duration: number
  orderIndex: number
  storyboardImageUrl?: string
  videoUrl?: string
  videoStatus: 'pending' | 'generating_image' | 'image_ready' | 'generating_video' | 'completed' | 'failed' | 'cancelled'
  metadata?: Record<string, unknown>
  generationStartedAt?: number
  sceneLabel?: string
  // Keyframes for precise video generation control
  keyframes?: Keyframe[]
  keyframeStatus?: 'none' | 'generating' | 'ready' | 'failed'
  startKeyframeUrl?: string
  startKeyframeSource?: string
  endKeyframeUrl?: string
  activeVideoId?: string
  videoVersionCount?: number // Number of video versions available
  // Video analysis fields
  analysis?: {
    score: number
    observations: string[]
    suggestions: string[]
    analyzedAt: number
  }
  analysisStatus?: 'pending' | 'analyzing' | 'completed' | 'failed'
  staleReasons?: string[]
  firstFrameUrl?: string
  lastFrameUrl?: string
  referenceFrameUrl?: string
  solidColor?: string
  dialog?: Array<{
    characterId: string
    characterName: string
    text: string
    tone?: string
  }>
  ttsStatus?: 'none' | 'pending' | 'generating' | 'ready' | 'failed'
  ttsAudioUrl?: string
  ttsAudioDuration?: number
  lipSyncStatus?: 'none' | 'pending' | 'processing' | 'mixing' | 'ready' | 'failed'
  lipSyncVideoUrl?: string
  nativeAudioEnabled?: boolean
  linkGroupId?: string
  transcriptionStatus?: 'none' | 'pending' | 'transcribing' | 'ready' | 'failed'
  transcription?: {
    words?: Array<{ w: string; s: number; e: number }>
    text: string
    language?: string
  }
  trimStart?: number
  trimEnd?: number
  speed?: number
  transition?: { type: string; duration: number }
  effects?: {
    fadeIn?: { color: string; duration: number }
    fadeOut?: { color: string; duration: number }
    colorGrade?: string
    letterbox?: boolean
    vignette?: number
    grain?: number
    blur?: number
    bloom?: number
    chromaticAberration?: number
    vhs?: boolean
    shake?: number
    kenBurns?: { startScale: number; endScale: number; startX?: number; startY?: number; endX?: number; endY?: number }
  }
}

interface AudioTrack {
  id: string
  type: 'music' | 'sfx' | 'voiceover'
  name: string
  url: string
  startTime: number
  duration: number
  trimStart?: number
  volume: number
  fadeIn?: number
  fadeOut?: number
  status: 'pending' | 'generating' | 'ready' | 'failed'
  metadata?: Record<string, unknown>
}

type AspectRatio = '16:9' | '9:16' | '1:1' | '4:5' | '4:3'

function EmphasisToast({ emphasis }: { emphasis: string | null }) {
  const [visible, setVisible] = useState<string | null>(null)
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (!emphasis) return
    setVisible(emphasis)
    setFading(false)
    const fadeTimer = setTimeout(() => setFading(true), 4000)
    const hideTimer = setTimeout(() => setVisible(null), 4700)
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer) }
  }, [emphasis])

  if (!visible) return null

  return (
    <div className={cn(
      "absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-foreground text-background text-xs font-medium shadow-lg transition-all duration-700",
      fading ? "opacity-0 -translate-y-1" : "opacity-100 translate-y-0 animate-in fade-in slide-in-from-top-3 duration-300"
    )}>
      <Sparkles size={12} className="shrink-0 opacity-70" />
      {visible}
    </div>
  )
}

function getThumbnailFit(aspectRatio?: string): string {
  return aspectRatio && aspectRatio !== '16:9' && aspectRatio !== '4:3' ? 'object-contain' : 'object-cover'
}

function getThumbnailBg(aspectRatio?: string): string {
  return aspectRatio && aspectRatio !== '16:9' && aspectRatio !== '4:3' ? 'bg-black' : 'bg-muted'
}

// ── Aspect Ratio Picker ──────────────────────────────────────

const ASPECT_RATIO_OPTIONS: { value: AspectRatio; label: string; sublabel: string; w: number; h: number }[] = [
  { value: '16:9', label: '16:9', sublabel: 'YouTube, Widescreen', w: 16, h: 9 },
  { value: '9:16', label: '9:16', sublabel: 'TikTok, Reels, Shorts', w: 9, h: 16 },
  { value: '1:1',  label: '1:1',  sublabel: 'Instagram, LinkedIn', w: 1, h: 1 },
  { value: '4:5',  label: '4:5',  sublabel: 'Instagram, Facebook Ads', w: 4, h: 5 },
  { value: '4:3',  label: '4:3',  sublabel: 'Classic, Presentations', w: 4, h: 3 },
]

function AspectRatioIcon({ w, h, size = 18, active = false }: { w: number; h: number; size?: number; active?: boolean }) {
  const maxDim = Math.max(w, h)
  const rw = (w / maxDim) * (size * 0.75)
  const rh = (h / maxDim) * (size * 0.75)
  const rx = (size - rw) / 2
  const ry = (size - rh) / 2
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      <rect
        x={rx} y={ry} width={rw} height={rh}
        rx={1.5}
        className={cn(
          'transition-colors',
          active ? 'fill-primary/20 stroke-primary' : 'fill-muted stroke-muted-foreground/40'
        )}
        strokeWidth={1.5}
      />
    </svg>
  )
}

function AspectRatioPicker({ value, onChange, clipCount }: {
  value: AspectRatio
  onChange: (ratio: AspectRatio) => void
  clipCount: number
}) {
  const [confirmTarget, setConfirmTarget] = useState<AspectRatio | null>(null)
  const current = ASPECT_RATIO_OPTIONS.find(o => o.value === value) ?? ASPECT_RATIO_OPTIONS[0]

  const handleSelect = (ratio: AspectRatio) => {
    if (ratio === value) return
    if (clipCount > 0) {
      setConfirmTarget(ratio)
    } else {
      onChange(ratio)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="inline-flex items-center gap-1.5 rounded-md h-7 px-2 text-[11px] font-medium border border-border bg-card text-secondary-foreground hover:bg-secondary transition-colors cursor-pointer">
            <AspectRatioIcon w={current.w} h={current.h} size={16} active />
            <span>{current.label}</span>
            <ChevronDown size={10} className="opacity-50" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[220px] p-1">
          {ASPECT_RATIO_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={cn(
                'flex items-center gap-2.5 px-2 py-1.5 rounded-sm cursor-pointer',
                opt.value === value && 'bg-primary/10'
              )}
            >
              <AspectRatioIcon w={opt.w} h={opt.h} size={20} active={opt.value === value} />
              <div className="flex flex-col min-w-0">
                <span className={cn('text-xs font-medium', opt.value === value && 'text-primary')}>{opt.label}</span>
                <span className="text-[10px] text-muted-foreground leading-tight">{opt.sublabel}</span>
              </div>
              {opt.value === value && <Check size={12} className="ml-auto text-primary shrink-0" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation dialog for ratio change with existing clips */}
      {confirmTarget && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in duration-150">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-[380px] p-5 animate-in zoom-in-95 duration-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
                <AlertTriangle size={16} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Change aspect ratio?</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {clipCount} clip{clipCount !== 1 ? 's' : ''} will be letterboxed to fit the new {confirmTarget} frame.
                  For best results, regenerate clips after changing.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-4 justify-center">
              <div className="flex flex-col items-center gap-1">
                <AspectRatioIcon w={current.w} h={current.h} size={36} />
                <span className="text-[10px] text-muted-foreground">{current.label}</span>
              </div>
              <ArrowRight size={14} className="text-muted-foreground" />
              <div className="flex flex-col items-center gap-1">
                <AspectRatioIcon w={ASPECT_RATIO_OPTIONS.find(o => o.value === confirmTarget)!.w} h={ASPECT_RATIO_OPTIONS.find(o => o.value === confirmTarget)!.h} size={36} active />
                <span className="text-[10px] text-primary font-medium">{confirmTarget}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmTarget(null)}
                className="flex-1 h-8 rounded-md border border-border text-xs font-medium text-secondary-foreground hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { onChange(confirmTarget); setConfirmTarget(null) }}
                className="flex-1 h-8 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:brightness-110 transition-all"
              >
                Change ratio
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// ── Export Settings Dropdown ──────────────────────────────────

type ExportResolution = '720p' | '1080p' | '4k'

const RESOLUTION_OPTIONS: { value: ExportResolution; label: string }[] = [
  { value: '720p',  label: '720p' },
  { value: '1080p', label: '1080p' },
  { value: '4k',    label: '4K' },
]

function getExportPixels(aspectRatio: AspectRatio, resolution: ExportResolution): string {
  const dims: Record<string, Record<string, string>> = {
    '16:9': { '720p': '1280 x 720', '1080p': '1920 x 1080', '4k': '3840 x 2160' },
    '9:16': { '720p': '720 x 1280', '1080p': '1080 x 1920', '4k': '2160 x 3840' },
    '1:1':  { '720p': '720 x 720',  '1080p': '1080 x 1080', '4k': '2160 x 2160' },
    '4:5':  { '720p': '576 x 720',  '1080p': '864 x 1080',  '4k': '1728 x 2160' },
    '4:3':  { '720p': '960 x 720',  '1080p': '1440 x 1080', '4k': '2880 x 2160' },
  }
  return dims[aspectRatio]?.[resolution] ?? dims['16:9'][resolution]
}

function ExportSettingsButton({
  canExport, hasExport, isExporting,
  exportDisabledReason, onExport, onReExport, aspectRatio,
}: {
  canExport: boolean; hasExport: boolean; isExporting: boolean
  exportDisabledReason: string | null
  onExport: (resolution: ExportResolution) => void
  onReExport: (resolution: ExportResolution) => void
  aspectRatio: AspectRatio
}) {
  const [resolution, setResolution] = useState<ExportResolution>('1080p')
  const [open, setOpen] = useState(false)
  const interactive = canExport && !isExporting
  const inFlightReason = isExporting ? 'Export in progress' : null
  const effectiveDisabledReason = exportDisabledReason ?? inFlightReason

  const handleExport = () => {
    if (!interactive) return
    setOpen(false)
    if (hasExport) onReExport(resolution)
    else onExport(resolution)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          title={effectiveDisabledReason ?? undefined}
          className={cn(
            "inline-flex items-center justify-center rounded-md h-7 px-3 text-[11px] font-semibold border transition-colors",
            interactive
              ? hasExport
                ? "border-border bg-card text-secondary-foreground hover:bg-secondary cursor-pointer"
                : "border-primary/40 bg-gradient-to-b from-primary to-primary/90 text-white hover:brightness-110 cursor-pointer shadow-sm shadow-primary/20"
              : "border-border bg-secondary text-muted-foreground cursor-default"
          )}
        >
          <Upload className="h-3 w-3 mr-1.5" />
          {isExporting ? 'Exporting…' : hasExport ? 'Re-export' : 'Export'}
          <ChevronDown size={10} className="ml-1 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px] p-0" onCloseAutoFocus={e => e.preventDefault()}>
        <div className="p-3 space-y-3" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Export Settings</span>
            <span className="text-[10px] text-muted-foreground font-mono">{getExportPixels(aspectRatio, resolution)}</span>
          </div>

          {/* Resolution */}
          <div className="space-y-1.5">
            <span className="text-[10px] text-muted-foreground font-medium">Resolution</span>
            <div className="flex gap-1">
              {RESOLUTION_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setResolution(opt.value)}
                  className={cn(
                    'flex-1 h-7 rounded-md text-[11px] font-medium border transition-all',
                    opt.value === resolution
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-secondary-foreground hover:bg-secondary'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Format info */}
          <div className="flex items-center justify-between pt-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono font-medium">MP4</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono font-medium">H.264</span>
            </div>
            <div className="flex items-center gap-1">
              <AspectRatioIcon w={ASPECT_RATIO_OPTIONS.find(o => o.value === aspectRatio)?.w ?? 16} h={ASPECT_RATIO_OPTIONS.find(o => o.value === aspectRatio)?.h ?? 9} size={14} active />
              <span className="text-[10px] text-muted-foreground">{aspectRatio}</span>
            </div>
          </div>

          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={!interactive}
            className={cn(
              'w-full h-8 rounded-md text-xs font-semibold transition-all',
              interactive
                ? 'bg-primary text-primary-foreground hover:brightness-110 cursor-pointer shadow-sm shadow-primary/20'
                : 'bg-secondary text-muted-foreground cursor-default'
            )}
          >
            {effectiveDisabledReason ? (
              effectiveDisabledReason
            ) : hasExport ? (
              'Re-export video'
            ) : (
              'Export video'
            )}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface WorkflowDocumentProps {
  workflowId: Id<"workflows">
  workflow: {
    status: string
    title?: string
    narrative: string
    characters: WorkflowCharacter[]
    globalContext?: string
    finalVideoUrl?: string
    finalVideoId?: string
    finalVideoStatus?: string
    finalVideoError?: string
    finalVideoProgress?: number
    audioTracks?: AudioTrack[]
    narratorVoice?: { voiceId: string; voiceProvider: string; voiceSampleUrl?: string }
    textOverlays?: TextOverlay[]
    projectTtsStatus?: string
    projectTtsTimestamps?: Array<{ word: string; startMs: number; endMs: number; inputIndex: number }>
    exportDuration?: number
    skillFeatures?: string[]
  }
  clips: WorkflowClip[]
  messages?: WorkflowMessage[]
  onNarrativeChange?: (narrative: string) => void
  credits?: number
  isAdmin?: boolean
  aspectRatio?: AspectRatio
  uiLayout?: UiLayoutState
  /** Admin: render only the video preview + timeline pair, full viewport. */
  timelineOnly?: boolean
}


// Hook to track elapsed time for a scene being rendered
// Uses server-side generationStartedAt timestamp for persistence across tab switches/reloads
function useElapsedTime(scene: WorkflowClip): { formatted: string | null; seconds: number | null } {
  const [elapsed, setElapsed] = useState<number | null>(null)
  const lastTimestampRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const isRendering = scene.videoStatus === 'generating_video' || scene.videoStatus === 'generating_image'

    if (!isRendering) {
      setElapsed(null)
      lastTimestampRef.current = undefined
      return
    }

    const startTime = scene.generationStartedAt || Date.now()

    if (scene.generationStartedAt !== lastTimestampRef.current) {
      lastTimestampRef.current = scene.generationStartedAt
      setElapsed(0)
    }

    const updateElapsed = () => {
      const seconds = Math.floor((Date.now() - startTime) / 1000)
      setElapsed(seconds)
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [scene._id, scene.videoStatus, scene.generationStartedAt])

  if (elapsed === null) return { formatted: null, seconds: null }

  const formatted = elapsed < 60
    ? `${elapsed}s`
    : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`

  return { formatted, seconds: elapsed }
}

function getRenderProgress(elapsedSeconds: number, expectedSeconds = 180) {
  const ratio = elapsedSeconds / expectedSeconds
  if (ratio <= 1) return 0.95 * (1 - (1 - ratio) ** 2)
  const overtime = (elapsedSeconds - expectedSeconds) / 60
  return 0.95 + 0.05 * (overtime / (overtime + 1))
}

function humanizeStaleReasons(reasons: string[], characters?: WorkflowCharacter[]): string {
  return reasons.map(r => {
    if (r === 'upstream_clip_changed') return 'Previous clip changed'
    if (r === 'global_context_changed') return 'Project context changed'
    if (r === 'settings_changed') return 'Video settings changed'
    if (r === 'dialog_changed') return 'Dialog edited after TTS'
    const charMatch = r.match(/^character_changed:(.+)$/)
    if (charMatch) {
      const name = characters?.find(c => c.id === charMatch[1])?.name ?? charMatch[1]
      return `Character '${name}' updated`
    }
    return r
  }).join(', ')
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  pending: { icon: <Clock size={12} />, label: 'Pending' },
  generating_image: { icon: <Loader2 size={12} className="animate-spin" />, label: 'Creating' },
  image_ready: { icon: <FileEdit size={12} />, label: 'Drafted' },
  generating_video: { icon: <Loader2 size={12} className="animate-spin" />, label: 'Rendering' },
  completed: { icon: <Check size={12} />, label: 'Done' },
  failed: { icon: <AlertCircle size={12} />, label: 'Failed' },
}

function SceneCard({ scene, index, onPlay, characters, isActive, hasContinuity, aspectRatio, allClips }: { scene: WorkflowClip; index: number; onPlay: (index: number) => void; characters?: WorkflowCharacter[]; isActive?: boolean; hasContinuity?: boolean; aspectRatio?: string; allClips?: WorkflowClip[] }) {
  const [showPromptShelf, setShowPromptShelf] = useState(false)
  const status = STATUS_CONFIG[scene.videoStatus] || STATUS_CONFIG.pending
  const isReady = scene.videoStatus === 'completed' || scene.videoStatus === 'image_ready'
  const { formatted: elapsedTime, seconds: elapsedSeconds } = useElapsedTime(scene)
  const isRendering = scene.videoStatus === 'generating_video' || scene.videoStatus === 'generating_image'
  const hasVideo = !!scene.videoUrl
  const hasStoryboard = !!scene.storyboardImageUrl
  const hasVersions = (scene.videoVersionCount ?? 0) > 1
  const isGeneratingKeyframes = scene.keyframeStatus === 'generating'

  // Get start and end keyframes for split preview (prefer asset-based URLs, fall back to legacy array)
  const startKeyframeUrl = scene.startKeyframeUrl || scene.keyframes?.find(kf => kf.timestamp === 0)?.url
  const endKeyframeUrl = scene.endKeyframeUrl || scene.keyframes?.find(kf => kf.timestamp === 1)?.url
  const hasKeyframes = !!(startKeyframeUrl && endKeyframeUrl)

  // Get characters in this clip for avatar display
  const clipCharacters = characters?.filter(c => scene.charactersInClip?.includes(c.id)) || []

  const isStale = (scene.staleReasons?.length ?? 0) > 0

  // Determine card border color based on status
  const borderClass = isActive
    ? 'border-primary/50'
    : scene.videoStatus === 'failed'
    ? 'border-destructive/30'
    : isRendering
    ? 'border-primary/30'
    : isStale && hasVideo
    ? 'border-orange-300/40'
    : hasVideo || !!scene.solidColor
    ? 'border-success/30'
    : hasKeyframes
    ? 'border-purple-300/40'
    : hasStoryboard
    ? 'border-amber-300/40'
    : 'border-border'

  const activeClass = isActive ? 'ring-2 ring-primary/30' : ''

  return (
    <div className="relative h-full">
      {/* Continuation connector from previous clip */}
      {hasContinuity && (
        <div className="absolute -left-[7px] top-1/3 -translate-y-1/2 z-10 pointer-events-none" aria-hidden>
          <ArrowRight size={10} className="text-teal-500" />
        </div>
      )}
      <div className={`bg-card rounded-lg border overflow-hidden transition-colors h-full flex flex-col ${borderClass} ${activeClass}`}>
      {/* Thumbnail - show keyframes split view, video, storyboard, or empty */}
      <button
        className={`aspect-video ${getThumbnailBg(aspectRatio)} relative w-full group cursor-pointer`}
        onClick={() => onPlay(index)}
        disabled={!isReady && !hasKeyframes}
      >
        {/* Priority: solid color > video > image clip > keyframes split view > storyboard > empty */}
        {scene.solidColor ? (
          <div className="w-full h-full" style={{ backgroundColor: scene.solidColor }} />
        ) : scene.videoUrl && !isImageOnlyClip(scene) ? (
          <video src={scene.videoUrl} className={`w-full h-full ${getThumbnailFit(aspectRatio)}`} muted preload="metadata" />
        ) : hasKeyframes ? (
          /* Keyframe transition preview (split view) */
          <div className="w-full h-full flex">
            <div className="w-1/2 h-full relative overflow-hidden">
              <img
                src={startKeyframeUrl}
                alt="Start keyframe"
                className="w-full h-full object-cover object-center"
                style={{ transform: 'translateX(25%)' }}
              />
              <div className="absolute inset-0 bg-linear-to-r from-transparent to-white/30" />
            </div>
            <div className="w-1/2 h-full relative overflow-hidden">
              <img
                src={endKeyframeUrl}
                alt="End keyframe"
                className="w-full h-full object-cover object-center"
                style={{ transform: 'translateX(-25%)' }}
              />
              <div className="absolute inset-0 bg-linear-to-l from-transparent to-white/30" />
            </div>
            {/* Transition arrow overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white/90 rounded-full p-1 shadow-sm">
                <ArrowRight size={12} className="text-purple-600" />
              </div>
            </div>
          </div>
        ) : (startKeyframeUrl || scene.storyboardImageUrl || scene.firstFrameUrl) ? (
          <>
            <img
              src={(startKeyframeUrl || scene.storyboardImageUrl || scene.firstFrameUrl)!}
              alt={scene.title || `Scene ${index + 1}`}
              className={`w-full h-full ${getThumbnailFit(aspectRatio)}`}
            />
            {!hasVideo && (
              <span className="absolute top-1 left-0 right-0 text-center text-[8px] font-medium tracking-wide text-gray-400/70 uppercase">Preview</span>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Film size={14} className="text-muted-foreground/60" />
          </div>
        )}

        {/* Keyframe generating overlay */}
        {isGeneratingKeyframes && (
          <div className="absolute inset-0 bg-purple-500/10 flex items-center justify-center">
            <div className="bg-white/90 rounded-full px-2 py-1 flex items-center gap-1">
              <Loader2 size={10} className="animate-spin text-purple-600" />
              <span className="text-[9px] text-purple-600 font-medium">Keyframes</span>
            </div>
          </div>
        )}

        {/* Storyboard-only overlay indicator — removed, "Preview" label replaces it */}

        {/* Low score overlay indicator - disabled */}

        {/* Subtle hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />

        {/* Duration badge */}
        {(() => {
          const eff = getEffectiveDuration(scene)
          const differs = Math.abs(eff - scene.duration) > 0.01
          const shots = (scene.metadata as Record<string, unknown>)?.shots
          return (
            <div
              className="absolute bottom-1 right-1 bg-black/40 text-white/80 text-[8px] font-medium px-1 py-0.5 rounded"
              title={differs ? `Raw: ${scene.duration}s → Effective: ${eff.toFixed(1)}s` : undefined}
            >
              {shots
                ? `${(shots as unknown[]).length} shots / ${differs ? eff.toFixed(1) : Number(scene.duration.toFixed(1))}s`
                : `${differs ? eff.toFixed(1) : Number(scene.duration.toFixed(1))}s`}
            </div>
          )
        })()}

        {/* Scene number */}
        <div className="absolute top-1 left-1 bg-black/40 text-white/80 text-[8px] font-semibold w-4 h-4 rounded flex items-center justify-center">
          {index + 1}
        </div>

        {/* Speed badge */}
        {scene.speed && scene.speed !== 1 && (
          <div className="absolute top-1 right-1 bg-orange-500/60 text-white/90 text-[8px] font-medium px-1 py-0.5 rounded flex items-center gap-0.5" title={`Speed: ${scene.speed}x`}>
            <Gauge size={8} />
            {scene.speed}x
          </div>
        )}

        {/* Generation mode indicators - bottom left */}
        <div className="absolute bottom-1 left-1 flex flex-wrap items-center gap-0.5">
          {/* Generation mode pill */}
          {(() => {
            const mode = resolveDisplayClipMode(scene.generationMode, !!hasContinuity, scene.startKeyframeSource)
            if (!mode) return null
            const modeConfig: Record<string, { label: string; bg: string; icon: React.ReactNode }> = {
              'text-to-video': { label: 'Text to Video', bg: 'bg-neutral-500/70', icon: <FileText size={8} /> },
              'generated-keyframe': { label: 'Generated Keyframe', bg: 'bg-indigo-500/70', icon: <Sparkles size={8} /> },
              'image-to-video': { label: 'Image to Video', bg: 'bg-blue-500/70', icon: <ImageIcon size={8} /> },
              'continuation': { label: 'Continuation from previous clip', bg: 'bg-teal-500/70', icon: <ArrowRight size={8} /> },
              'reference-to-video': { label: 'Character Reference', bg: 'bg-emerald-500/70', icon: <UserRound size={8} /> },
              'first-last-frame': { label: 'Keyframe Transition', bg: 'bg-purple-500/70', icon: <ArrowRight size={8} /> },
              'edit-video': { label: 'Edit Video', bg: 'bg-amber-500/70', icon: <FileEdit size={8} /> },
              'video-reference': { label: 'Video Style Reference', bg: 'bg-teal-500/70', icon: <Video size={8} /> },
              'avatar': { label: 'Avatar', bg: 'bg-violet-500/70', icon: <Mic size={8} /> },
              'uploaded': { label: 'Uploaded', bg: 'bg-stone-500/70', icon: <ArrowUpFromLine size={8} /> },
            }
            const cfg = modeConfig[mode]
            if (!cfg) return null
            return (
              <TooltipProvider delayDuration={100} key={`mode-${mode}`}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`${cfg.bg} text-white px-1 py-0.5 rounded text-[8px] font-medium leading-none inline-flex items-center`}>
                      {cfg.icon}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">{cfg.label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}

          {/* Transcription badge */}
          {scene.transcriptionStatus === 'ready' && scene.transcription?.text && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="bg-sky-500/70 text-white px-1 py-0.5 rounded text-[8px] font-medium leading-none flex items-center">
                    <Captions size={8} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs line-clamp-2">{scene.transcription.text}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {scene.transcriptionStatus === 'transcribing' && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="bg-sky-500/70 text-white px-1 py-0.5 rounded text-[8px] font-medium leading-none flex items-center">
                    <Loader2 size={8} className="animate-spin" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Transcribing audio...</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Character avatars - show characters in this clip (also indicates reference-to-video when used) */}
          {clipCharacters.length > 0 && (
            <div className={`flex -space-x-1.5 ${scene.generationMode === 'reference-to-video' ? 'ring-1 ring-green-400 rounded-full' : ''}`} title={scene.generationMode === 'reference-to-video' ? 'Reference-to-video: using character references' : 'Characters in clip'}>
              {clipCharacters.slice(0, 3).map((char) => {
                const avatarImage = char.referenceImages?.[0]?.url
                return avatarImage ? (
                  <img
                    key={char.id}
                    src={avatarImage}
                    alt={char.name}
                    title={char.name}
                    className="w-4 h-4 rounded-full object-cover border border-white/70 shadow-sm"
                  />
                ) : (
                  <div
                    key={char.id}
                    title={char.name}
                    className="w-4 h-4 rounded-full bg-surface-elevated flex items-center justify-center text-white text-[7px] font-medium border border-white/70 shadow-sm"
                  >
                    {char.name.charAt(0).toUpperCase()}
                  </div>
                )
              })}
              {clipCharacters.length > 3 && (
                <div className="w-4 h-4 rounded-full bg-surface-muted flex items-center justify-center text-white text-[7px] font-medium border border-white/70 shadow-sm">
                  +{clipCharacters.length - 3}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Version selector - show when multiple versions exist */}
        {hasVersions && (
          <VersionSelector
            clipId={scene._id as Id<"workflowClips">}
            versionCount={scene.videoVersionCount!}
          />
        )}

        {/* Keyframe indicator badge */}
        {hasKeyframes && (
          <div className={`absolute top-1 ${hasVersions ? 'right-12' : 'right-1'} bg-purple-500/70 text-white text-[8px] px-1 py-0.5 rounded flex items-center gap-0.5`}>
            <Sparkles size={7} />
            <span>KF</span>
          </div>
        )}

        {/* Download button - only show when video is ready */}
        {hasVideo && scene.videoUrl && (
          <button
            className={`absolute ${
              hasVersions && hasKeyframes
                ? 'top-1 right-23'
                : hasVersions || hasKeyframes
                  ? 'top-1 right-12'
                  : 'top-1 right-1'
            } bg-black/40 hover:bg-black/70 text-white/80 hover:text-white p-1 rounded transition-all opacity-0 group-hover:opacity-100`}
            onClick={async (e) => {
              e.stopPropagation()
              try {
                await downloadVideo(scene.videoUrl!, `${scene.title || `scene-${index + 1}`}.mp4`)
              } catch (err) {
                console.error('Download failed:', err)
              }
            }}
            title="Download clip"
          >
            <Download size={12} />
          </button>
        )}
      </button>

      {/* Info */}
      <div className="p-2 flex-1 flex flex-col">
        {/* Prompt summary */}
        {scene.promptSummary && (
          <p className="text-[11px] text-muted-foreground line-clamp-3 leading-snug">
            {scene.promptSummary}
          </p>
        )}

        {/* Dialog is shown in the Script panel, not per-clip */}

        {/* Effects indicators */}
        {(scene.effects || scene.transition) && (
          <div className="flex items-center gap-0.5 mt-1 flex-wrap">
            {scene.effects?.colorGrade && (
              <span className="inline-flex items-center gap-0.5 text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">
                <Palette size={7} />
                {scene.effects.colorGrade}
              </span>
            )}
            {scene.effects?.letterbox && (
              <span className="inline-flex items-center gap-0.5 text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">letterbox</span>
            )}
            {scene.effects?.fadeIn && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">fade in</span>
            )}
            {scene.effects?.fadeOut && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">fade out</span>
            )}
            {scene.effects?.vignette && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">vignette</span>
            )}
            {scene.effects?.blur != null && scene.effects.blur > 0 && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">blur</span>
            )}
            {scene.effects?.bloom != null && scene.effects.bloom > 0 && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">bloom</span>
            )}
            {scene.effects?.chromaticAberration != null && scene.effects.chromaticAberration > 0 && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">chromatic</span>
            )}
            {scene.effects?.vhs && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">VHS</span>
            )}
            {scene.effects?.shake != null && scene.effects.shake > 0 && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">shake</span>
            )}
            {scene.effects?.kenBurns && (
              <span className="inline-flex items-center text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">
                {matchMotionPreset(scene.effects.kenBurns)?.name ?? 'motion'}
              </span>
            )}
            {scene.transition && (
              <span className="inline-flex items-center gap-0.5 text-[8px] px-1 py-px rounded bg-muted text-muted-foreground">
                <Diamond size={7} />
                {scene.transition.type} ({scene.transition.duration}s)
              </span>
            )}
          </div>
        )}

        {/* Status and Prompt button row */}
        <div className="flex items-center justify-between mt-auto pt-1.5">
          {/* Status badge */}
          <div className="flex items-center gap-1">
            {isStale && hasVideo && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">
                      <AlertTriangle size={12} className="text-orange-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {humanizeStaleReasons(scene.staleReasons ?? [], characters)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isRendering ? (
              <div className="relative z-0 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded overflow-hidden bg-blue-50">
                <span
                  className="absolute inset-y-0 left-0 bg-blue-100/60 rounded transition-[width] duration-1000 ease-out"
                  style={{ width: elapsedSeconds != null ? `${(getRenderProgress(elapsedSeconds) * 100).toFixed(1)}%` : '0%' }}
                />
                <Loader2 size={9} className="animate-spin text-blue-600 relative z-10" />
                <span className="ml-0.5 text-blue-600 relative z-10">Rendering</span>
                {elapsedTime && <span className="text-blue-500 tabular-nums ml-0.5 relative z-10">{elapsedTime}</span>}
              </div>
            ) : (
              <div className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded ${
                isStale && hasVideo ? 'bg-orange-50 text-orange-600' :
                scene.videoStatus === 'completed' ? 'bg-success/8 text-success' :
                scene.videoStatus === 'failed' ? 'bg-destructive/8 text-destructive' :
                hasKeyframes ? 'bg-purple-50 text-purple-600' :
                scene.videoStatus === 'image_ready' ? 'bg-purple-50 text-purple-600' :
                'bg-muted text-muted-foreground'
              }`}>
                {hasKeyframes && scene.videoStatus !== 'completed' ? (
                  <>
                    <Sparkles size={10} />
                    <span className="ml-0.5">Keyframes Ready</span>
                  </>
                ) : isStale && hasVideo ? (
                  <span>Stale</span>
                ) : (
                  <>
                    {status.icon}
                    <span className="ml-0.5">{status.label}</span>
                  </>
                )}
                {elapsedTime && (
                  <span className="text-muted-foreground font-medium ml-0.5 tabular-nums">{elapsedTime}</span>
                )}
              </div>
            )}
          </div>

          {/* Prompt button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowPromptShelf(true)
            }}
            className="text-muted-foreground/50 hover:text-secondary-foreground hover:bg-muted p-0.5 rounded transition-colors"
            title="View and edit prompt"
          >
            <FileEdit size={11} />
          </button>
        </div>
      </div>

      {/* Prompt shelf */}
      {showPromptShelf && (
        <PromptShelf scene={scene} onClose={() => setShowPromptShelf(false)} characters={characters} allClips={allClips} />
      )}
      </div>
    </div>
  )
}

// Debounce hook for prompt editing
function useDebouncedCallback<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => callbackRef.current(...args), delay)
    }) as T,
    [delay]
  )
}

function MultiShotEditor({ scene }: { scene: WorkflowClip }) {
  const updateClipShots = useMutation(api.workflows.updateClipShots)
  const serverShots = (scene.metadata as Record<string, unknown>)?.shots as Array<{ prompt: string; duration: number }> | undefined
  const [localShots, setLocalShots] = useState(serverShots)
  const localRef = useRef(localShots)
  localRef.current = localShots

  useEffect(() => {
    setLocalShots(serverShots)
  }, [JSON.stringify(serverShots)])

  const debouncedSave = useDebouncedCallback((updated: Array<{ prompt: string; duration: number }>) => {
    updateClipShots({ clipId: scene._id as Id<"workflowClips">, shots: updated })
  }, 800)

  if (!localShots || localShots.length === 0) return null

  const totalDuration = localShots.reduce((sum, s) => sum + s.duration, 0)

  const updateShot = (index: number, field: 'prompt' | 'duration', value: string | number) => {
    const updated = localShots.map((s, i) => i === index ? { ...s, [field]: value } : s)
    setLocalShots(updated)
    if (field === 'duration') {
      updateClipShots({ clipId: scene._id as Id<"workflowClips">, shots: updated })
    } else {
      debouncedSave(updated)
    }
  }

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Multi-Shot Storyboard
        </label>
        <span className="text-xs text-muted-foreground">
          {localShots.length} shots / {totalDuration}s total
        </span>
      </div>
      <div className="space-y-2">
        {localShots.map((shot, i) => (
          <div key={i} className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold text-cyan-700 uppercase">Shot {i + 1}</span>
              <select
                value={shot.duration}
                onChange={(e) => updateShot(i, 'duration', Number(e.target.value))}
                className="text-[10px] bg-cyan-100 text-cyan-600 px-1.5 py-0.5 rounded font-medium border-0 cursor-pointer focus:ring-1 focus:ring-cyan-400"
              >
                {Array.from({ length: 13 }, (_, d) => d + 3).map(d => (
                  <option key={d} value={d}>{d}s</option>
                ))}
              </select>
            </div>
            <textarea
              value={shot.prompt}
              onChange={(e) => updateShot(i, 'prompt', e.target.value)}
              rows={3}
              className="w-full text-sm text-secondary-foreground leading-relaxed bg-transparent border-0 resize-none focus:outline-none focus:ring-0 p-0"
            />
          </div>
        ))}
      </div>
    </div>
  )
}

function KeyframeSourceLabel({ source }: { source?: string }) {
  if (!source) return null
  const labels: Record<string, { text: string; color: string }> = {
    extracted: { text: 'Extracted', color: 'text-teal-600 bg-teal-50' },
    generated: { text: 'Generated', color: 'text-indigo-600 bg-indigo-50' },
    uploaded: { text: 'Uploaded', color: 'text-blue-600 bg-blue-50' },
  }
  const cfg = labels[source]
  if (!cfg) return null
  return <span className={cn('text-[9px] font-medium px-1.5 py-0.5 rounded-full', cfg.color)}>{cfg.text}</span>
}

function GenerationSourcePreview({ scene, characters, startKeyframeUrl, endKeyframeUrl }: {
  scene: WorkflowClip
  characters?: WorkflowCharacter[]
  startKeyframeUrl?: string
  endKeyframeUrl?: string
}) {
  const mode = scene.generationMode
  const clipChars = characters?.filter(c => scene.charactersInClip?.includes(c.id)) || []
  const charRefs = clipChars.flatMap(c => (c.referenceImages || []).slice(0, 1).map(img => ({ url: img.url, name: c.name })))
  const hasBothKeyframes = !!(startKeyframeUrl && endKeyframeUrl)
  const hasAnyKeyframe = !!(startKeyframeUrl || endKeyframeUrl)

  const modeDisplay: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    'text-to-video': { label: 'Text to Video', color: 'text-muted-foreground bg-muted border-border', icon: <FileText size={13} /> },
    'image-to-video': { label: 'Image to Video', color: 'text-blue-600 bg-blue-50 border-blue-200', icon: <ImageIcon size={13} /> },
    'reference-to-video': { label: 'Character Reference', color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: <UserRound size={13} /> },
    'first-last-frame': { label: 'Keyframe Transition', color: 'text-purple-600 bg-purple-50 border-purple-200', icon: <ArrowRight size={13} /> },
    'edit-video': { label: 'Edit Video', color: 'text-amber-600 bg-amber-50 border-amber-200', icon: <FileEdit size={13} /> },
    'video-reference': { label: 'Video Style Ref', color: 'text-teal-600 bg-teal-50 border-teal-200', icon: <Video size={13} /> },
    'avatar': { label: 'Avatar', color: 'text-violet-600 bg-violet-50 border-violet-200', icon: <Mic size={13} /> },
    'multi-shot': { label: 'Multi-Shot', color: 'text-cyan-600 bg-cyan-50 border-cyan-200', icon: <Layers size={13} /> },
  }

  const cfg = mode ? modeDisplay[mode] : null
  const hasVisualInputs = hasAnyKeyframe || (mode === 'reference-to-video' && charRefs.length > 0)

  if (!hasVisualInputs && !cfg) return null

  return (
    <div className="px-4 pt-3 pb-3 border-b border-border">
      {cfg && (
        <div className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md border mb-3', cfg.color)}>
          {cfg.icon}
          {cfg.label}
        </div>
      )}

      {mode === 'text-to-video' && !hasAnyKeyframe && (
        <div className="flex items-center gap-2 text-muted-foreground text-xs py-1">
          <span>Generated from the text prompt below</span>
        </div>
      )}

      {mode === 'reference-to-video' && (
        <div className="flex items-center gap-2">
          {charRefs.map((ref, i) => (
            <div key={i} className="flex flex-col items-center gap-1 shrink-0">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-emerald-300 bg-muted">
                <img src={ref.url} alt={ref.name} className="w-full h-full object-cover" />
              </div>
              <span className="text-[9px] text-muted-foreground font-medium">{ref.name}</span>
            </div>
          ))}
          {hasAnyKeyframe && charRefs.length > 0 && (
            <ArrowRight size={12} className="text-emerald-400 shrink-0" />
          )}
          {hasBothKeyframes ? (
            <>
              <div className="w-56 shrink-0">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="text-[9px] font-medium text-muted-foreground uppercase">Start</span>
                  <KeyframeSourceLabel source={scene.startKeyframeSource} />
                </div>
                <div className="aspect-video rounded overflow-hidden bg-muted border border-border">
                  <img src={startKeyframeUrl} alt="Start keyframe" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="flex flex-col items-center shrink-0">
                <ArrowRight size={12} className="text-muted-foreground/60" />
                <span className="text-[8px] text-muted-foreground">{scene.duration}s</span>
              </div>
              <div className="w-56 shrink-0">
                <span className="text-[9px] font-medium text-muted-foreground uppercase block mb-0.5">End</span>
                <div className="aspect-video rounded overflow-hidden bg-muted border border-border">
                  <img src={endKeyframeUrl} alt="End keyframe" className="w-full h-full object-cover" />
                </div>
              </div>
            </>
          ) : startKeyframeUrl ? (
            <div className="w-56 shrink-0">
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[9px] font-medium text-muted-foreground uppercase">Start</span>
                <KeyframeSourceLabel source={scene.startKeyframeSource} />
              </div>
              <div className="aspect-video rounded overflow-hidden bg-muted border border-border">
                <img src={startKeyframeUrl} alt="Start keyframe" className="w-full h-full object-cover" />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {mode !== 'reference-to-video' && mode !== 'text-to-video' && hasBothKeyframes && (
        <div className="flex items-center gap-2">
          <div className="w-64 shrink-0">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[9px] font-medium text-muted-foreground uppercase">Start Frame</span>
              <KeyframeSourceLabel source={scene.startKeyframeSource} />
            </div>
            <div className="aspect-video rounded overflow-hidden bg-muted border border-border">
              <img src={startKeyframeUrl} alt="Start keyframe" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="flex flex-col items-center shrink-0">
            <ArrowRight size={14} className="text-purple-400" />
            <span className="text-[8px] text-muted-foreground">{scene.duration}s</span>
          </div>
          <div className="w-64 shrink-0">
            <span className="text-[9px] font-medium text-muted-foreground uppercase block mb-1">End Frame</span>
            <div className="aspect-video rounded overflow-hidden bg-muted border border-border">
              <img src={endKeyframeUrl} alt="End keyframe" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      )}

      {mode !== 'reference-to-video' && mode !== 'text-to-video' && !hasBothKeyframes && hasAnyKeyframe && (
        <div className="w-80">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-[9px] font-medium text-muted-foreground uppercase">{startKeyframeUrl ? 'Start Frame' : 'End Frame'}</span>
            {startKeyframeUrl && <KeyframeSourceLabel source={scene.startKeyframeSource} />}
          </div>
          <div className="aspect-video rounded-lg overflow-hidden bg-muted border border-border">
            <img src={startKeyframeUrl || endKeyframeUrl} alt="Keyframe" className="w-full h-full object-cover" />
          </div>
        </div>
      )}
    </div>
  )
}

function TranscriptWords({ scene, allClips }: { scene: WorkflowClip; allClips?: WorkflowClip[] }) {
  const seekContext = useVideoSeekOptional()
  const currentTime = seekContext?.currentTime ?? 0

  const words = scene.transcription?.words
  const trimStart = scene.trimStart || 0
  const trimEnd = scene.trimEnd || 0
  const speed = scene.speed || 1

  const clipGlobalStart = useMemo(() => {
    if (!allClips?.length) return 0
    const model = buildTimelineModel(allClips as unknown as TimelineClip[])
    const entry = model.entries.find(e => e.clip._id === scene._id)
    return entry?.globalStart ?? 0
  }, [allClips, scene._id])

  const toGlobalTime = useCallback((localSeconds: number) => {
    return clipGlobalStart + (localSeconds - trimStart) / speed
  }, [clipGlobalStart, trimStart, speed])

  const clipEnd = scene.duration - trimEnd

  return (
    <div className="mt-4">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Captions size={12} />
        Transcript
        {scene.transcription?.language && (
          <span className="text-muted-foreground font-normal">({scene.transcription.language})</span>
        )}
      </label>
      <div className="bg-secondary/50 border border-border rounded-lg p-2.5">
        {words?.length ? (
          <p className="text-sm leading-relaxed">
            {words.map((word, i) => {
              if (word.s < trimStart || word.e > clipEnd) return null
              const globalStart = toGlobalTime(word.s)
              const globalEnd = toGlobalTime(word.e)
              const isActive = currentTime >= globalStart && currentTime < globalEnd
              return (
                <span
                  key={i}
                  onClick={() => seekContext?.seekTo(globalStart)}
                  className={cn(
                    'cursor-pointer rounded-sm px-px transition-colors',
                    isActive
                      ? 'bg-primary/20 text-primary font-medium'
                      : 'text-secondary-foreground hover:bg-muted'
                  )}
                >
                  {word.w}{' '}
                </span>
              )
            })}
          </p>
        ) : (
          <p className="text-sm text-secondary-foreground leading-relaxed">{scene.transcription?.text}</p>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Auto-transcribed from generated audio.{words?.length ? ` ${words.length} words detected.` : ''}{' '}
        {words?.length ? 'Click a word to seek.' : ''}
      </p>
    </div>
  )
}

// Bottom shelf to display and edit prompt
export function PromptShelf({ scene, onClose, characters, allClips }: { scene: WorkflowClip; onClose: () => void; characters?: WorkflowCharacter[]; allClips?: WorkflowClip[] }) {
  const updateScenePrompt = useMutation(api.workflows.updateScenePrompt)
  const [isSaving, setIsSaving] = useState(false)
  const prompt = scene.videoPrompt || scene.visualDescription || ''

  const legacyStartKeyframe = scene.keyframes?.find(kf => kf.timestamp === 0)?.url
  const legacyEndKeyframe = scene.keyframes?.find(kf => kf.timestamp === 1)?.url
  const startKeyframeUrl = scene.startKeyframeUrl || legacyStartKeyframe
  const endKeyframeUrl = scene.endKeyframeUrl || legacyEndKeyframe

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const debouncedSave = useDebouncedCallback(async (html: string) => {
    // Convert paragraph breaks to newlines, then strip remaining HTML tags
    const plainText = html
      .replace(/<\/p>\s*<p>/g, '\n')  // Convert paragraph breaks to newlines
      .replace(/<[^>]*>/g, '')         // Strip remaining HTML tags
      .trim()
    if (plainText === prompt) return

    setIsSaving(true)
    try {
      await updateScenePrompt({
        sceneId: scene._id as Id<"workflowClips">,
        videoPrompt: plainText,
      })
    } catch (error) {
      console.error('Failed to save prompt:', error)
    } finally {
      setIsSaving(false)
    }
  }, 1000)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Describe what happens in this clip...',
      }),
    ],
    content: prompt.split('\n').filter(p => p.trim()).map(p => `<p>${p}</p>`).join('') || '<p></p>',
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      debouncedSave(editor.getHTML())
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-0 p-3 text-secondary-foreground text-sm leading-relaxed',
      },
    },
  })

  const status = STATUS_CONFIG[scene.videoStatus] || STATUS_CONFIG.pending
  const isRendering = scene.videoStatus === 'generating_video' || scene.videoStatus === 'generating_image'

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-neutral-200/60 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-[760px] max-h-[85vh] flex flex-col bg-card border border-neutral-300 rounded-2xl shadow-[0_12px_30px_-12px_rgba(0,0,0,0.14)] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                scene.videoStatus === 'completed' ? 'bg-success/10 text-success' :
                scene.videoStatus === 'failed' ? 'bg-destructive/10 text-destructive' :
                scene.videoStatus === 'image_ready' ? 'bg-purple-50 text-purple-700' :
                isRendering ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {status.icon}
                <span>{status.label}</span>
              </div>
              {isSaving && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" />
                  Saving...
                </span>
              )}
            </div>
            {scene.promptSummary && (
              <p className="text-base text-secondary-foreground leading-relaxed">{scene.promptSummary}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-secondary-foreground transition-colors p-1 -mr-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <GenerationSourcePreview scene={scene} characters={characters} startKeyframeUrl={startKeyframeUrl} endKeyframeUrl={endKeyframeUrl} />

          <div className="px-5 py-4">
            <MultiShotEditor scene={scene} />

            {!((scene.metadata as Record<string, unknown>)?.shots) && (
              <>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                  Video Prompt
                </label>
                <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-colors">
                  <EditorContent editor={editor} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  This prompt will be used to generate the video for this clip.
                </p>
              </>
            )}

            {scene.transcriptionStatus === 'ready' && scene.transcription?.text && (
              <TranscriptWords scene={scene} allClips={allClips} />
            )}
            {scene.transcriptionStatus === 'transcribing' && (
              <div className="mt-4">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Captions size={12} />
                  Transcript
                </label>
                <div className="bg-secondary border border-border rounded-lg p-2.5 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Transcribing audio...</span>
                </div>
              </div>
            )}

            {(() => {
              const clipCharacters = characters?.filter(c => scene.charactersInClip?.includes(c.id)) || []
              if (clipCharacters.length === 0) return null
              return (
                <div className="mt-4">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 block">
                    Characters
                  </label>
                  <div className="bg-muted rounded-lg border border-border p-3 text-sm text-secondary-foreground leading-relaxed whitespace-pre-wrap">
                    {clipCharacters.map((char, i) => (
                      <span key={char.id}>
                        <span className="font-medium text-foreground">{char.name}</span>
                        {char.description && `: ${char.description}`}
                        {i < clipCharacters.length - 1 && '\n\n'}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Included automatically when generating.
                  </p>
                </div>
              )
            })()}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

type GroupMode = 'clips' | 'scenes'

function useGridColumns(containerRef: React.RefObject<HTMLDivElement | null>): number {
  const [columns, setColumns] = useState(3)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateColumns = () => {
      const width = container.offsetWidth
      if (width >= 1536) setColumns(5)
      else if (width >= 1280) setColumns(4)
      else setColumns(3)
    }

    updateColumns()
    const observer = new ResizeObserver(updateColumns)
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef])

  return columns
}

function SceneCardWithLabel({
  clip,
  index,
  onPlay,
  characters,
  label,
  isStartOfSegment,
  isEndOfSegment,
  isActive,
  hasContinuity,
  aspectRatio,
  allClips,
}: {
  clip: WorkflowClip
  index: number
  onPlay: (index: number) => void
  characters?: WorkflowCharacter[]
  label: string | null
  isStartOfSegment: boolean
  isEndOfSegment: boolean
  isActive?: boolean
  hasContinuity?: boolean
  aspectRatio?: string
  allClips?: WorkflowClip[]
}) {
  if (!label) {
    return (
      <div className="flex flex-col">
        <div className="h-5 mb-1 shrink-0" />
        <div className="flex-1 min-h-0">
          <SceneCard scene={clip} index={index} onPlay={onPlay} characters={characters} isActive={isActive} hasContinuity={hasContinuity} aspectRatio={aspectRatio} allClips={allClips} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div
        className={`flex items-center h-5 mb-1 shrink-0 ${
          isStartOfSegment ? '' : '-ml-[5px]'
        } ${
          isEndOfSegment ? '' : '-mr-[5px]'
        }`}
      >
        {isStartOfSegment && <div className="w-1 shrink-0" />}
        <div className="flex-1 h-px bg-border/60" />
        {isStartOfSegment && (
          <span className="text-[9px] font-medium text-muted-foreground/80 px-1.5 shrink-0 whitespace-nowrap uppercase tracking-wide">
            {label}
          </span>
        )}
        <div className="flex-1 h-px bg-border/60" />
        {isEndOfSegment && <div className="w-1 shrink-0" />}
      </div>
      <div className="flex-1 min-h-0">
        <SceneCard scene={clip} index={index} onPlay={onPlay} characters={characters} isActive={isActive} hasContinuity={hasContinuity} aspectRatio={aspectRatio} />
      </div>
    </div>
  )
}

function SceneStackCard({
  sceneLabel,
  clips,
  sceneIndex,
  onPlay,
  aspectRatio,
}: {
  sceneLabel: string
  clips: WorkflowClip[]
  sceneIndex: number
  onPlay: (clipIndex: number) => void
  aspectRatio?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const firstClip = clips[0]
  const clipCount = clips.length
  const videoCount = clips.filter(c => c.videoUrl).length
  const storyboardCount = clips.filter(c => c.storyboardImageUrl).length
  const totalDuration = clips.reduce((sum, c) => sum + c.duration, 0)
  const allHaveVideo = videoCount === clipCount
  const allHaveStoryboard = storyboardCount === clipCount
  const hasAnyFailed = clips.some(c => c.videoStatus === 'failed')
  const hasAnyRendering = clips.some(c => c.videoStatus === 'generating_video' || c.videoStatus === 'generating_image')

  const summaries = clips
    .map((clip, i) => clip.promptSummary ? { index: i + 1, summary: clip.promptSummary } : null)
    .filter(Boolean) as { index: number; summary: string }[]

  const borderClass = hasAnyFailed
    ? 'border-destructive/30'
    : hasAnyRendering
    ? 'border-primary/30'
    : allHaveVideo
    ? 'border-success/30'
    : allHaveStoryboard
    ? 'border-amber-200'
    : 'border-border'

  return (
    <div className={`bg-card rounded-md border-2 overflow-hidden transition-colors ${borderClass}`}>
      <button
        className={`aspect-video ${getThumbnailBg(aspectRatio)} relative w-full group cursor-pointer`}
        onClick={() => onPlay(sceneIndex)}
      >
        <div className="absolute -top-1 left-1 right-1 h-2 bg-muted rounded-t-sm opacity-60" />
        <div className="absolute -top-0.5 left-0.5 right-0.5 h-1 bg-border rounded-t-sm opacity-40" />

        <div className="relative w-full h-full">
          {firstClip.videoUrl && !isImageOnlyClip(firstClip) ? (
            <video src={firstClip.videoUrl} className={`w-full h-full ${getThumbnailFit(aspectRatio)}`} muted preload="metadata" />
          ) : (firstClip.storyboardImageUrl || firstClip.firstFrameUrl) ? (
            <>
              <img src={(firstClip.storyboardImageUrl || firstClip.firstFrameUrl)!} alt={sceneLabel} className={`w-full h-full ${getThumbnailFit(aspectRatio)}`} />
              {!allHaveVideo && (
                <span className="absolute top-1 left-0 right-0 text-center text-[8px] font-medium tracking-wide text-gray-400/70 uppercase z-10">Preview</span>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film size={14} className="text-muted-foreground/60" />
            </div>
          )}

          {/* Amber overlay removed — "Preview" label replaces it */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />

          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
            <Layers size={10} />
            {clipCount} clips
          </div>
          <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-medium px-1 py-0.5 rounded">
            {sceneIndex + 1}
          </div>
          <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] px-1 py-0.5 rounded">
            {totalDuration}s
          </div>
        </div>
      </button>

      <div className="p-2">
        <h4 className="font-medium text-secondary-foreground text-xs truncate">{sceneLabel}</h4>
        {summaries.length > 0 && (
          <div className="mt-1.5">
            {expanded ? (
              <div className="space-y-1">
                {summaries.map(({ index, summary }) => (
                  <p key={index} className="text-[10px] text-muted-foreground leading-relaxed">
                    <span className="font-medium text-secondary-foreground">{index}.</span> {summary}
                  </p>
                ))}
                <button onClick={(e) => { e.stopPropagation(); setExpanded(false) }} className="text-[10px] text-muted-foreground hover:text-secondary-foreground">
                  Show less
                </button>
              </div>
            ) : (
              <div>
                <p className="text-[10px] text-muted-foreground line-clamp-2 leading-relaxed">
                  {summaries.map(({ index, summary }) => `${index}. ${summary}`).join(' · ')}
                </p>
                {summaries.length > 1 && (
                  <button onClick={(e) => { e.stopPropagation(); setExpanded(true) }} className="text-[10px] text-muted-foreground hover:text-secondary-foreground mt-0.5">
                    Show all {summaries.length} clips
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function SceneStackWithLabel({
  sceneLabel,
  clips,
  sceneIndex,
  onPlay,
  isStartOfSegment,
  isEndOfSegment,
  aspectRatio,
}: {
  sceneLabel: string
  clips: WorkflowClip[]
  sceneIndex: number
  onPlay: (clipIndex: number) => void
  isStartOfSegment: boolean
  isEndOfSegment: boolean
  aspectRatio?: string
}) {
  return (
    <div className="flex flex-col">
      <div
        className={`flex items-center h-5 mb-1 ${
          isStartOfSegment ? '' : '-ml-[5px]'
        } ${
          isEndOfSegment ? '' : '-mr-[5px]'
        }`}
      >
        {isStartOfSegment && <div className="w-1.5 shrink-0" />}
        <div className="flex-1 h-px bg-border" />
        {isStartOfSegment && (
          <span className="text-[10px] font-medium text-muted-foreground px-1.5 shrink-0 whitespace-nowrap">
            {sceneLabel}
          </span>
        )}
        <div className="flex-1 h-px bg-border" />
        {isEndOfSegment && <div className="w-1.5 shrink-0" />}
      </div>
      <SceneStackCard sceneLabel={sceneLabel} clips={clips} sceneIndex={sceneIndex} onPlay={onPlay} aspectRatio={aspectRatio} />
    </div>
  )
}

function ClipsPanel({
  clips,
  onClipClick,
  characters,
  activeClipIndex,
  aspectRatio,
  transitionPickerClipId,
  onTransitionPickerChange,
}: {
  clips: WorkflowClip[]
  onClipClick?: (index: number) => void
  characters?: WorkflowCharacter[]
  activeClipIndex?: number
  aspectRatio?: string
  transitionPickerClipId: string | null
  onTransitionPickerChange: (clipId: string | null) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const columns = useGridColumns(containerRef)
  const [groupMode, setGroupMode] = useState<GroupMode>('clips')

  const playableClips = clips.filter(c => c.videoUrl || c.storyboardImageUrl || c.startKeyframeUrl || c.firstFrameUrl)
  const activeClipId = activeClipIndex !== undefined ? playableClips[activeClipIndex]?._id : undefined

  if (clips.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-xs">
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4">
            <Film size={24} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            Your clip timeline will appear here
          </p>
        </div>
      </div>
    )
  }

  const handlePlay = (index: number) => {
    onClipClick?.(index)
  }

  const hasAnyLabels = clips.some(c => c.sceneLabel)

  const groupedScenes = hasAnyLabels ? clips.reduce((acc, clip) => {
    const label = clip.sceneLabel || 'Unlabeled'
    if (!acc[label]) acc[label] = []
    acc[label].push(clip)
    return acc
  }, {} as Record<string, WorkflowClip[]>) : {}

  const sceneLabels = Object.keys(groupedScenes)

  return (
    <div className="space-y-2.5" ref={containerRef}>
      {hasAnyLabels && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 p-0.5 bg-muted/60 rounded-lg border border-border/50">
            <button
              onClick={() => setGroupMode('clips')}
              className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all ${
                groupMode === 'clips'
                  ? 'bg-background text-secondary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-secondary-foreground'
              }`}
            >
              <Film size={11} />
              Clips
            </button>
            <button
              onClick={() => setGroupMode('scenes')}
              className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md transition-all ${
                groupMode === 'scenes'
                  ? 'bg-background text-secondary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-secondary-foreground'
              }`}
            >
              <Layers size={11} />
              Scenes
              <span className="text-[10px] text-muted-foreground/70">({sceneLabels.length})</span>
            </button>
          </div>
        </div>
      )}

      {groupMode === 'scenes' && hasAnyLabels ? (
        <div className="grid grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-2 gap-y-3">
          {sceneLabels.map((label, sceneIndex) => {
            const sceneClips = groupedScenes[label]
            const firstClipIndex = clips.findIndex(c => c._id === sceneClips[0]._id)
            return (
              <SceneStackWithLabel
                key={label}
                sceneLabel={label}
                clips={sceneClips}
                sceneIndex={sceneIndex}
                onPlay={() => handlePlay(firstClipIndex)}
                isStartOfSegment={true}
                isEndOfSegment={true}
                aspectRatio={aspectRatio}
              />
            )
          })}
        </div>
      ) : hasAnyLabels ? (
        <div className="grid grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-2 gap-y-3">
          {clips.map((clip, index) => {
            const label = clip.sceneLabel || null
            const colInRow = index % columns
            const prevInRow = colInRow > 0 ? clips[index - 1] : null
            const nextInRow = colInRow < columns - 1 && index + 1 < clips.length ? clips[index + 1] : null
            const isStartOfSegment = !prevInRow || prevInRow.sceneLabel !== clip.sceneLabel
            const isEndOfSegment = !nextInRow || nextInRow.sceneLabel !== clip.sceneLabel
            const prevClip = index > 0 ? clips[index - 1] : null
            const hasContinuity = hasClipContinuity(clip, prevClip)

            return (
              <SceneCardWithLabel
                key={clip._id}
                clip={clip}
                index={index}
                onPlay={handlePlay}
                characters={characters}
                label={label}
                isStartOfSegment={isStartOfSegment}
                isEndOfSegment={isEndOfSegment}
                isActive={clip._id === activeClipId}
                hasContinuity={hasContinuity}
                aspectRatio={aspectRatio}
                allClips={clips}
              />
            )
          })}
        </div>
      ) : (
        <div className="grid grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-x-4 gap-y-2">
          {clips.map((clip, index) => {
            const prevClip = index > 0 ? clips[index - 1] : null
            const hasContinuity = hasClipContinuity(clip, prevClip)
            const colInRow = index % columns
            const showLeftIndicator = index > 0 && colInRow > 0
            return (
              <div key={clip._id} className="relative h-full">
                {showLeftIndicator && (
                  <div className="absolute left-0 top-0 z-20 w-full aspect-video pointer-events-none">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-auto">
                      <TransitionIndicator
                        transition={clip.transition}
                        onClick={() => onTransitionPickerChange(transitionPickerClipId === clip._id ? null : clip._id)}
                      />
                    </div>
                  </div>
                )}
                {index > 0 && colInRow === 0 && (
                  <div className="absolute -top-1.5 left-1 z-20">
                    <TransitionIndicator
                      transition={clip.transition}
                      onClick={() => onTransitionPickerChange(transitionPickerClipId === clip._id ? null : clip._id)}
                    />
                  </div>
                )}
                <SceneCard
                  scene={clip}
                  index={index}
                  onPlay={handlePlay}
                  characters={characters}
                  isActive={clip._id === activeClipId}
                  hasContinuity={hasContinuity}
                  aspectRatio={aspectRatio}
                  allClips={clips}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function VideoTimelineContainer({ children, timelineExpanded, onCollapseTimeline }: { children: React.ReactNode; timelineExpanded: boolean; onCollapseTimeline: () => void }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!timelineExpanded || !ref.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.height < 200) {
          onCollapseTimeline()
        }
      }
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [timelineExpanded, onCollapseTimeline])

  return (
    <div ref={ref} className="h-full flex flex-col overflow-hidden relative">
      {children}
    </div>
  )
}

function VideoPreviewPanel({
  clips,
  workflow,
  workflowId,
  activeSceneIndex,
  onSceneChange,
  aspectRatio,
  onSpatialUpdate,
  selectedSpatialId,
  onSpatialSelect,
}: {
  clips: WorkflowClip[]
  workflow: { title?: string; finalVideoUrl?: string; finalVideoId?: string; finalVideoStatus?: string; finalVideoError?: string; finalVideoProgress?: number; audioTracks?: AudioTrack[]; textOverlays?: Array<{ id: string; text: string; startTime: number; duration: number; position?: string; style?: string; color?: string; spatialX?: number; spatialY?: number; spatialWidth?: number; spatialHeight?: number; spatialRotation?: number }>; timelineMode?: 'ripple' | 'preserve_positions'; demoStyle?: import('@/remotion/demo/types').DemoStyle }
  workflowId: Id<"workflows">
  activeSceneIndex?: number
  onSceneChange?: (index: number) => void
  aspectRatio?: string
  onSpatialUpdate: (itemId: string, itemType: 'clip' | 'text', changes: Record<string, number>) => void
  selectedSpatialId: string | null
  onSpatialSelect: (id: string | null) => void
}) {
  const updateAudioTrack = useMutation(api.audioGeneration.updateAudioTrackSettings)

  const handleAudioTrackUpdate = async (trackId: string, startTime: number, duration: number, trimStart?: number) => {
    await updateAudioTrack({
      workflowId,
      trackId,
      startTime,
      duration,
      ...(trimStart !== undefined && { trimStart }),
    })
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-[200px]">
          <VideoPreview
            scenes={clips}
            activeSceneIndex={activeSceneIndex}
            onSceneChange={onSceneChange}
            audioTracks={workflow.audioTracks}
            textOverlays={workflow.textOverlays}
            demoStyle={workflow.demoStyle}
            onAudioTrackUpdate={handleAudioTrackUpdate}
            aspectRatio={aspectRatio}
            timelineMode={(workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode}
            onSpatialUpdate={onSpatialUpdate}
            selectedSpatialId={selectedSpatialId}
            onSpatialSelect={onSpatialSelect}
          />
        </div>
      </div>
    </div>
  )
}

/* TimelinePanel - no longer used, video preview is now always at top
function TimelinePanel({...}) {...}
*/

// Legacy UndoAction type — replaced by projectStore undo/redo system.
// The store captures patches/inversePatches automatically via Mutative.

function relativeTime(ts: number): string {
  const diff = Date.now() - ts
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(ts).toLocaleDateString()
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `0:${s.toString().padStart(2, '0')}`
}

class QueryErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(_: Error, info: ErrorInfo) { console.warn('QueryErrorBoundary caught:', info) }
  render() { return this.state.hasError ? null : this.props.children }
}

export function ExportsDropdown({
  workflowId,
  workflow,
  triggerClassName,
  onReExport,
  reExportDisabled,
}: {
  workflowId: Id<"workflows">
  workflow: { title?: string; finalVideoUrl?: string; finalVideoId?: string; shortcode?: string }
  triggerClassName?: string
  onReExport?: () => void
  reExportDisabled?: boolean
}) {
  const exports = useQuery(api.videoStitching.listExports, { workflowId })
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxExportId, setLightboxExportId] = useState<string | null>(null)
  const { activeFeedbackExport, dismiss: dismissFeedback } = useExportFeedbackGate(workflowId, exports)

  if (!exports || exports.length === 0) return null

  const handleDownload = async (videoUrl: string) => {
    await downloadVideo(videoUrl, `${workflow.title || 'footage-export'}.mp4`)
  }

  const hasProcessing = exports.some(e => e.status === 'processing' || e.status === 'pending')

  function getProgressLabel(exp: { progress?: number | null; status: string; createdAt: number }) {
    if (exp.status === 'pending') return 'Starting export...'
    const p = exp.progress
    if (p === -1) return 'Queued'
    if (p === undefined || p === null || p === 0) {
      // Renders without granular progress (single GPU chunk) sit at 0 for many
      // minutes — show "Rendering..." after the first 30s so the user knows
      // it's working, not stuck.
      const ageMs = Date.now() - exp.createdAt
      return ageMs > 30_000 ? 'Rendering...' : 'Starting export...'
    }
    if (p <= 5) return 'Preparing assets...'
    if (p <= 10) return 'Rendering...'
    if (p < 85) return `Rendering... ${p}%`
    if (p < 95) return 'Stitching...'
    if (p >= 95) return 'Uploading...'
    return `Processing... ${p}%`
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={triggerClassName ?? "inline-flex items-center justify-center rounded-lg h-8 pl-4 pr-3 text-xs font-semibold border border-surface-border bg-gradient-to-b from-surface-elevated to-surface text-white shadow-sm hover:brightness-110 transition-colors gap-1"}
          >
            {hasProcessing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Video className="h-3 w-3" />
            )}
            Exports ({exports.length})
            <ChevronDown className="h-3 w-3 text-surface-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {exports.map((exp, i) => {
            const isProcessing = exp.status === 'processing' || exp.status === 'pending'
            const isQueued = exp.progress === -1
            return isProcessing ? (
              <div key={exp._id} className="flex items-center gap-3 py-2 px-2">
                <div className="w-10 h-10 rounded bg-surface flex items-center justify-center shrink-0">
                  {isQueued ? (
                    <Clock size={14} className="text-surface-muted" />
                  ) : (
                    <Loader2 size={14} className="text-surface-muted animate-spin" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-secondary-foreground truncate">
                    {getProgressLabel(exp)}
                  </div>
                  {exp.progress != null && exp.progress > 0 && exp.progress < 100 && (
                    <div className="mt-1 h-1 rounded-full bg-surface overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                        style={{ width: `${exp.progress}%` }}
                      />
                    </div>
                  )}
                  {isQueued && (
                    <div className="text-[11px] text-muted-foreground">
                      Waiting for render slot{typeof exp.queuePosition === 'number' && exp.queuePosition > 0 ? ` (${exp.queuePosition} ahead)` : ''}
                    </div>
                  )}
                  {!isQueued && (exp.progress == null || exp.progress === 0) && (
                    <div className="text-[11px] text-muted-foreground">
                      {Date.now() - exp.createdAt > 30_000 ? 'Render in progress, this may take several minutes' : 'Just started'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <DropdownMenuItem
                key={exp._id}
                className="flex items-center gap-3 py-2 cursor-pointer"
                onClick={() => {
                  if (exp.videoUrl) {
                    setLightboxUrl(exp.videoUrl)
                    setLightboxExportId(exp._id)
                  }
                }}
              >
                <div className="w-10 h-10 rounded bg-surface flex items-center justify-center shrink-0">
                  {exp.thumbnailUrl ? (
                    <img src={exp.thumbnailUrl} className="w-full h-full rounded object-cover" />
                  ) : (
                    <Play size={14} className="text-surface-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-secondary-foreground truncate">
                    Export {exports.length - i}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span>{relativeTime(exp.createdAt)}</span>
                    {exp.duration > 0 && (
                      <>
                        <span className="text-border">·</span>
                        <span>{formatDuration(exp.duration)}</span>
                      </>
                    )}
                  </div>
                </div>
                {exp.videoUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownload(exp.videoUrl!)
                    }}
                    className="p-1 rounded hover:bg-surface text-muted-foreground hover:text-white transition-colors shrink-0"
                  >
                    <Download size={14} />
                  </button>
                )}
              </DropdownMenuItem>
            )
          })}
          {onReExport && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={reExportDisabled || hasProcessing}
                className="flex items-center gap-2 py-2 cursor-pointer"
                onClick={() => onReExport()}
              >
                <RotateCcw size={14} className="text-muted-foreground" />
                <span className="text-xs font-medium">Re-export</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {lightboxUrl && (
        <ExportLightbox
          url={lightboxUrl}
          exportId={lightboxExportId}
          onClose={() => { setLightboxUrl(null); setLightboxExportId(null) }}
          onDownload={() => handleDownload(lightboxUrl)}
          onShare={() => {
            const shareCode = workflow.shortcode ?? workflowId
            if (shareCode) window.open(`${window.location.origin}/share/${shareCode}`, '_blank')
          }}
        />
      )}

      {activeFeedbackExport && (
        <ExportFeedbackModal
          key={activeFeedbackExport._id}
          workflowId={workflowId}
          exportId={activeFeedbackExport._id}
          onDismiss={() => dismissFeedback(activeFeedbackExport._id)}
        />
      )}
    </>
  )
}

type ExportClip = {
  videoStatus?: string
  videoUrl?: string
  storyboardImageUrl?: string
  firstFrameUrl?: string
}

export function ExportButton({
  workflowId,
  workflow,
  clips,
}: {
  workflowId: Id<"workflows"> | null
  workflow: { title?: string; finalVideoUrl?: string; finalVideoId?: string; shortcode?: string }
  clips: ExportClip[] | undefined
}) {
  const exports = useQuery(api.videoStitching.listExports, workflowId ? { workflowId } : 'skip')
  const startStitching = useAction(api.videoStitching.startStitching)
  const reExport = useAction(api.videoStitching.reExport)
  const videoSeekContext = useVideoSeekOptional()
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [lightboxExportId, setLightboxExportId] = useState<string | null>(null)
  const { activeFeedbackExport, dismiss: dismissFeedback } = useExportFeedbackGate(workflowId, exports)

  const exportsLoading = !!workflowId && exports === undefined
  const hasExport = !!(exports && exports.length > 0)
  const isExporting = !!(exports?.some(e => e.status === 'processing' || e.status === 'pending'))
  const hasClips = !!(clips && clips.length > 0)
  const allClipsComplete = hasClips && clips!.every(s => s.videoStatus === 'completed' && (s.videoUrl || s.storyboardImageUrl || s.firstFrameUrl))
  const canExport = !!workflowId && allClipsComplete
  const incompleteClips = clips?.filter(s => s.videoStatus !== 'completed' || !(s.videoUrl || s.storyboardImageUrl || s.firstFrameUrl)) ?? []
  const incompleteCount = incompleteClips.length
  const activelyGenerating = incompleteClips.filter(s => s.videoStatus === 'generating_image' || s.videoStatus === 'generating_video').length
  const disabledReason = !workflowId || !hasClips
    ? 'Nothing to export yet'
    : !allClipsComplete
      ? activelyGenerating > 0
        ? `${activelyGenerating} clip${activelyGenerating !== 1 ? 's' : ''} generating`
        : `${incompleteCount} clip${incompleteCount !== 1 ? 's' : ''} not ready`
      : null

  const getAudioMixArgs = () => {
    const mix = videoSeekContext?.audioMix
    if (!mix) return undefined
    const hasChanges = Object.values(mix.mutedTracks).some(Boolean) ||
      Object.values(mix.trackVolumes).some(v => v !== 1) ||
      Object.values(mix.clipMutes).some(Boolean) ||
      Object.values(mix.clipVolumes).some(v => v !== 1)
    return hasChanges ? mix : undefined
  }

  const runExport = async (kind: 'export' | 'reexport') => {
    if (!workflowId) return
    try {
      if (kind === 'export') await startStitching({ workflowId, audioMix: getAudioMixArgs() })
      else await reExport({ workflowId, audioMix: getAudioMixArgs() })
    } catch (error) {
      const raw = error instanceof Error ? error.message : String(error)
      const short = raw.length > 160 ? raw.slice(0, 160) + '...' : raw
      toast({ title: 'Export failed', description: short, variant: 'destructive' })
    }
  }

  const handleDownload = async (videoUrl: string) => {
    await downloadVideo(videoUrl, `${workflow.title || 'footage-export'}.mp4`)
  }

  const getProgressLabel = (exp: { progress?: number | null; status: string; createdAt: number }) => {
    if (exp.status === 'pending') return 'Starting export...'
    const p = exp.progress
    if (p === -1) return 'Queued'
    if (p === undefined || p === null || p === 0) {
      const ageMs = Date.now() - exp.createdAt
      return ageMs > 30_000 ? 'Rendering...' : 'Starting export...'
    }
    if (p <= 5) return 'Preparing assets...'
    if (p <= 10) return 'Rendering...'
    if (p < 85) return `Rendering... ${p}%`
    if (p < 95) return 'Stitching...'
    if (p >= 95) return 'Uploading...'
    return `Processing... ${p}%`
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium border border-zinc-200/80 bg-white text-zinc-900 hover:bg-zinc-50 shadow-sm transition-colors"
          >
            {isExporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            Export
            <ChevronDown className="h-3 w-3 ml-0.5 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[260px] p-1.5">
          {exportsLoading && (
            <div className="w-full flex items-center justify-center h-8 text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
          )}

          {!exportsLoading && !hasExport && disabledReason && (
            <div className="w-full inline-flex items-center justify-center h-8 px-3 rounded-md bg-zinc-50 text-[13px] font-medium text-zinc-500">
              {disabledReason}
            </div>
          )}

          {!exportsLoading && !hasExport && !disabledReason && (
            <DropdownMenuItem
              disabled={isExporting}
              className="flex items-center gap-2 py-2 cursor-pointer"
              onClick={() => void runExport('export')}
            >
              <Upload size={14} className="text-muted-foreground" />
              <span className="text-xs font-medium">Export video</span>
            </DropdownMenuItem>
          )}

          {hasExport && exports!.map((exp, i) => {
            const isProcessing = exp.status === 'processing' || exp.status === 'pending'
            const isQueued = exp.progress === -1
            return isProcessing ? (
              <div key={exp._id} className="flex items-center gap-3 py-2 px-2">
                <div className="w-10 h-10 rounded bg-surface flex items-center justify-center shrink-0">
                  {isQueued ? (
                    <Clock size={14} className="text-surface-muted" />
                  ) : (
                    <Loader2 size={14} className="text-surface-muted animate-spin" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-secondary-foreground truncate">
                    {getProgressLabel(exp)}
                  </div>
                  {exp.progress != null && exp.progress > 0 && exp.progress < 100 && (
                    <div className="mt-1 h-1 rounded-full bg-surface overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                        style={{ width: `${exp.progress}%` }}
                      />
                    </div>
                  )}
                  {isQueued && (
                    <div className="text-[11px] text-muted-foreground">
                      Waiting for render slot{typeof exp.queuePosition === 'number' && exp.queuePosition > 0 ? ` (${exp.queuePosition} ahead)` : ''}
                    </div>
                  )}
                  {!isQueued && (exp.progress == null || exp.progress === 0) && (
                    <div className="text-[11px] text-muted-foreground">
                      {Date.now() - exp.createdAt > 30_000 ? 'Render in progress, this may take several minutes' : 'Just started'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <DropdownMenuItem
                key={exp._id}
                className="flex items-center gap-3 py-2 cursor-pointer"
                onClick={() => {
                  if (exp.videoUrl) {
                    setLightboxUrl(exp.videoUrl)
                    setLightboxExportId(exp._id)
                  }
                }}
              >
                <div className="w-10 h-10 rounded bg-surface flex items-center justify-center shrink-0">
                  {exp.thumbnailUrl ? (
                    <img src={exp.thumbnailUrl} className="w-full h-full rounded object-cover" />
                  ) : (
                    <Play size={14} className="text-surface-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-secondary-foreground truncate">
                    Export {exports!.length - i}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span>{relativeTime(exp.createdAt)}</span>
                    {exp.duration > 0 && (
                      <>
                        <span className="text-border">·</span>
                        <span>{formatDuration(exp.duration)}</span>
                      </>
                    )}
                  </div>
                </div>
                {exp.videoUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownload(exp.videoUrl!)
                    }}
                    className="p-1 rounded hover:bg-surface text-muted-foreground hover:text-white transition-colors shrink-0"
                  >
                    <Download size={14} />
                  </button>
                )}
              </DropdownMenuItem>
            )
          })}
          {hasExport && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!canExport || isExporting}
                className="flex items-center gap-2 py-2 cursor-pointer"
                onClick={() => void runExport('reexport')}
              >
                <RotateCcw size={14} className="text-muted-foreground" />
                <span className="text-xs font-medium">Re-export</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {lightboxUrl && (
        <ExportLightbox
          url={lightboxUrl}
          exportId={lightboxExportId}
          onClose={() => { setLightboxUrl(null); setLightboxExportId(null) }}
          onDownload={() => handleDownload(lightboxUrl)}
          onShare={() => {
            const shareCode = workflow.shortcode ?? workflowId
            if (shareCode) window.open(`${window.location.origin}/share/${shareCode}`, '_blank')
          }}
        />
      )}

      {activeFeedbackExport && workflowId && (
        <ExportFeedbackModal
          key={activeFeedbackExport._id}
          workflowId={workflowId}
          exportId={activeFeedbackExport._id}
          onDismiss={() => dismissFeedback(activeFeedbackExport._id)}
        />
      )}
    </>
  )
}

function ExportLightbox({
  url,
  exportId,
  onClose,
  onDownload,
  onShare,
}: {
  url: string
  exportId: string | null
  onClose: () => void
  onDownload: () => void
  onShare: () => void
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey, true)
    return () => document.removeEventListener('keydown', handleKey, true)
  }, [onClose])

  useEffect(() => {
    const stop = (e: Event) => {
      const root = document.getElementById('export-lightbox-root')
      if (root?.contains(e.target as Node)) {
        e.stopPropagation()
      }
    }
    document.addEventListener('pointerdown', stop, true)
    document.addEventListener('mousedown', stop, true)
    return () => {
      document.removeEventListener('pointerdown', stop, true)
      document.removeEventListener('mousedown', stop, true)
    }
  }, [])

  return createPortal(
    <div
      id="export-lightbox-root"
      className="fixed inset-0 z-100 flex flex-col items-center justify-center bg-black/90 animate-in fade-in duration-200 cursor-pointer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose() }}
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors z-10"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      <div
        className="relative animate-in slide-in-from-bottom-4 fade-in duration-300 ease-out cursor-default flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <video
          src={url}
          controls
          autoPlay
          className="max-w-[85vw] max-h-[75vh] object-contain rounded-lg"
        />

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onDownload}
            className="border-white/20 bg-white/10 text-white hover:bg-white/20 h-8 text-xs"
          >
            <Download className="h-3 w-3 mr-1.5" />
            Download
          </Button>
          {exportId && (
            <Button
              size="sm"
              variant="outline"
              onClick={onShare}
              className="border-white/20 bg-white/10 text-white hover:bg-white/20 h-8 text-xs"
            >
              <ExternalLink className="h-3 w-3 mr-1.5" />
              Share
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export function WorkflowDocument({ workflowId, workflow, clips, messages = [], onNarrativeChange, isAdmin = false, aspectRatio, uiLayout, timelineOnly = false }: WorkflowDocumentProps) {
  const [selectedClipIndex, setSelectedClipIndex] = useState<number | null>(null)
  const playableClips = useMemo(() => (clips || []).filter(c => c.videoUrl || c.storyboardImageUrl || c.startKeyframeUrl || c.firstFrameUrl), [clips])
  const selectedPlayableIndex = useMemo(() => {
    if (selectedClipIndex == null) return undefined
    const clip = (clips || [])[selectedClipIndex]
    if (!clip) return undefined
    const idx = playableClips.findIndex(c => c._id === clip._id)
    return idx >= 0 ? idx : undefined
  }, [selectedClipIndex, clips, playableClips])
  const [transitionPickerClipId, setTransitionPickerClipId] = useState<string | null>(null)
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null)
  const [selectedSpatialId, setSelectedSpatialId] = useState<string | null>(null)
  const updateClipSpatial = useMutation(api.workflows.updateClipSpatial)
  const updateTextOverlaySpatial = useMutation(api.workflows.updateTextOverlay)
  const [timelineExpanded, setTimelineExpanded] = useState(false)
  const [clipsCollapsed, setClipsCollapsed] = useState(false)
  const videoSeekContext = useVideoSeekOptional()
  const [showStoryDrawer, setShowStoryDrawer] = useState(false)
  const storyGroupRef = useRef<GroupImperativeHandle>(null)
  const videoClipsGroupRef = useRef<GroupImperativeHandle>(null)
  const [expandedCharacterId, setExpandedCharacterId] = useState<string | null>(null)
  const [regeneratingCharacterId, setRegeneratingCharacterId] = useState<string | null>(null)
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string; pose: string } | null>(null)

  useEffect(() => {
    if (!uiLayout || !storyGroupRef.current) return
    const h = uiLayout.storyPanelHeight
    if (h > 0) {
      storyGroupRef.current.setLayout({ 'story-panel': h, 'workflow-content': 100 - h })
      setShowStoryDrawer(true)
    } else if (h === 0 && !expandedCharacterId) {
      storyGroupRef.current.setLayout({ 'story-panel': 0, 'workflow-content': 100 })
      setShowStoryDrawer(false)
    }
  }, [uiLayout?.storyPanelHeight])

  useEffect(() => {
    if (!uiLayout || !videoClipsGroupRef.current) return
    if (!uiLayout.clipsPanelVisible) {
      videoClipsGroupRef.current.setLayout({ 'video-preview': 100, 'clips-panel': 0 })
      setClipsCollapsed(true)
    } else {
      const split = uiLayout.videoClipsSplit
      videoClipsGroupRef.current.setLayout({ 'video-preview': split, 'clips-panel': 100 - split })
      setClipsCollapsed(false)
    }
  }, [uiLayout?.clipsPanelVisible, uiLayout?.videoClipsSplit])

  const startStitching = useAction(api.videoStitching.startStitching)
  const reExport = useAction(api.videoStitching.reExport)
  const regenerateCharacterRefs = useAction(api.workflows.regenerateCharacterReferences)
  const previewVoice = useAction(api.ttsGeneration.previewVoice)
  const hasTtsFallback = !!useFF('tts_fallback')
  const updateCharacter = useMutation(api.workflows.updateCharacter)
  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  // Access store actions via getState() for stable references (no re-render on state change)
  const ps = useProjectStore
  const sendMessageMutation = useMutation(api.workflows.sendMessage)

  const audioTrackTranscriptions = useQuery(api.transcription.getAudioTrackTranscriptions, { workflowId }) ?? {}
  const audioFirstClips = (workflow.skillFeatures || []).includes('audio-first-clips')
  const hasAudioTracks = (workflow.audioTracks || []).length > 0
  const hasTranscription = (clips || []).some(c => c.transcriptionStatus === 'ready' && (c.transcription as any)?.words?.length)

  const handleAudioUpload = useCallback(async (files: File[]) => {
    const file = files[0]
    if (!file) return
    try {
      const uploadUrl = await generateUploadUrl()
      const result = await fetch(uploadUrl, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type },
      })
      if (!result.ok) throw new Error('Upload failed')
      const { storageId } = await result.json()
      const url = await getStorageUrl({ storageId })
      if (!url) throw new Error('Failed to get storage URL')

      const audio = new Audio()
      const dur = await new Promise<number>((resolve, reject) => {
        audio.onloadedmetadata = () => resolve(audio.duration)
        audio.onerror = () => reject(new Error('Cannot read audio'))
        audio.src = URL.createObjectURL(file)
      })
      URL.revokeObjectURL(audio.src)

      await sendMessageMutation({
        workflowId,
        content: `I uploaded a podcast audio file: ${file.name}`,
        attachments: [{
          type: 'audio' as const,
          url,
          storageId,
          name: file.name,
          duration: dur,
          mimeType: file.type,
        }],
      })
      toast({ title: 'Audio uploaded', description: `${file.name} is being processed.` })
    } catch (e) {
      toast({ title: 'Upload failed', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' })
    }
  }, [workflowId, generateUploadUrl, getStorageUrl, sendMessageMutation])

  const handleSelectSegment = useCallback(async (startTime: number, endTime: number, text: string) => {
    const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
    await sendMessageMutation({
      workflowId,
      content: `Create a clip from ${fmtTime(startTime)} to ${fmtTime(endTime)}: "${text}"`,
    })
  }, [workflowId, sendMessageMutation])

  const handleDeleteClips = useCallback(async (clipIds: string[]) => {
    for (const clipId of clipIds) {
      ps.getState().deleteClip(clipId)
    }
  }, [])

  const handleDeleteAudioTracks = useCallback(async (trackIds: string[]) => {
    for (const trackId of trackIds) {
      ps.getState().removeAudioTrack(trackId)
    }
  }, [])

  const handleAudioTrackUpdateWithUndo = useCallback(async (trackId: string, startTime: number, duration: number, trimStart?: number) => {
    ps.getState().updateAudioTrack(trackId, {
      startTime,
      duration,
      ...(trimStart !== undefined && { trimStart }),
    })
  }, [])

  const handleEnvelopeChange = useCallback(async (trackId: string, envelope: Array<{ time: number; volume: number }>) => {
    ps.getState().updateAudioTrack(trackId, { volumeEnvelope: envelope })
  }, [])

  const handleClipReorder = useCallback(async (newOrder: string[]) => {
    ps.getState().reorderClips(newOrder)
  }, [])

  const handleClipTrimUpdate = useCallback(async (clipId: string, trimStart: number, trimEnd: number) => {
    ps.getState().trimClip(clipId, trimStart, trimEnd)
  }, [])

  const handleTextOverlayUpdate = useCallback(async (overlayId: string, startTime: number, duration: number) => {
    ps.getState().updateTextOverlay(overlayId, { startTime, duration })
  }, [])

  const handleRemoveTextOverlay = useCallback(async (overlayId: string) => {
    ps.getState().removeTextOverlay(overlayId)
    if (selectedOverlayId === overlayId) setSelectedOverlayId(null)
  }, [selectedOverlayId])

  const handleOverlayStyleUpdate = useCallback((overlayId: string, updates: Record<string, any>) => {
    ps.getState().updateTextOverlay(overlayId, updates)
  }, [])

  const handleUndo = useCallback(() => {
    ps.getState().undo()
  }, [])

  const handleRedo = useCallback(() => {
    ps.getState().redo()
  }, [])

  const handleExportDurationChange = useCallback((duration: number | undefined) => {
    ps.getState().updateExportDuration(duration)
  }, [])

  // Keyboard shortcuts for undo/redo are handled by useUndoRedoShortcuts() at the page level

  const handleVoiceClone = useCallback(async (file: File, characterId?: string) => {
    const uploadUrl = await generateUploadUrl()
    const res = await fetch(uploadUrl, { method: 'POST', headers: { 'Content-Type': file.type }, body: file })
    const { storageId } = await res.json()
    const url = await getStorageUrl({ storageId })
    if (!url) throw new Error('Failed to get storage URL')
    if (characterId) {
      await updateCharacter({ workflowId, characterId, voiceSampleUrl: url })
    }
  }, [generateUploadUrl, getStorageUrl, updateCharacter, workflowId])

  const handleClipClick = useCallback((index: number) => {
    setSelectedClipIndex(index)
    setSelectedOverlayId(null)
    const allClips = clips || []
    const clickedClip = allClips[index]
    if (clickedClip?.videoUrl) {
      track('clip.previewed', { workflowId: workflowId as string, clipId: clickedClip._id })
    }
    const playableClips = allClips.filter(c => c.videoUrl || c.storyboardImageUrl || c.startKeyframeUrl || c.firstFrameUrl)
    const playableIndex = clickedClip ? playableClips.findIndex(c => c._id === clickedClip._id) : -1
    let startTime = 0
    for (let i = 0; i < playableIndex && i < playableClips.length; i++) {
      startTime += playableClips[i].duration || 5
    }
    if (playableIndex >= 0) videoSeekContext?.seekTo(startTime)
  }, [clips, videoSeekContext, workflowId])

  const handleSceneChange = useCallback((playableIdx: number) => {
    const clip = playableClips[playableIdx]
    if (clip) {
      const fullIdx = (clips || []).findIndex(c => c._id === clip._id)
      setSelectedClipIndex(fullIdx >= 0 ? fullIdx : playableIdx)
    } else {
      setSelectedClipIndex(playableIdx)
    }
  }, [clips, playableClips])

  const handleSpatialUpdate = useCallback(async (itemId: string, itemType: 'clip' | 'text', changes: Record<string, number>) => {
    if (itemType === 'clip') {
      await updateClipSpatial({ clipId: itemId as Id<"workflowClips">, ...changes } as any)
    } else {
      await updateTextOverlaySpatial({ workflowId, overlayId: itemId, ...changes } as any)
    }
  }, [updateClipSpatial, updateTextOverlaySpatial, workflowId])



  // Export state
  const allClipsComplete = clips && clips.length > 0 && clips.every(s => s.videoStatus === 'completed' && (s.videoUrl || s.storyboardImageUrl || s.firstFrameUrl))
  const hasClips = clips && clips.length > 0
  const allExports = useQuery(api.videoStitching.listExports, { workflowId })
  const hasExport = !!(allExports && allExports.length > 0)
  const isExporting = !!(allExports?.some(e => e.status === 'processing' || e.status === 'pending'))
  const canExport = allClipsComplete
  const incompleteClips = clips?.filter(s => s.videoStatus !== 'completed' || !(s.videoUrl || s.storyboardImageUrl || s.firstFrameUrl)) ?? []
  const incompleteCount = incompleteClips.length
  const activelyGenerating = incompleteClips.filter(s => s.videoStatus === 'generating_image' || s.videoStatus === 'generating_video').length
  const exportDisabledReason = !hasClips
    ? 'No clips to export'
    : !allClipsComplete
      ? activelyGenerating > 0
        ? `${activelyGenerating} clip${activelyGenerating !== 1 ? 's' : ''} generating`
        : `${incompleteCount} clip${incompleteCount !== 1 ? 's' : ''} not ready`
      : null

  const prevExportStatus = useRef(workflow.finalVideoStatus)
  useEffect(() => {
    if (workflow.finalVideoStatus === 'failed' && prevExportStatus.current !== 'failed') {
      const raw = workflow.finalVideoError || 'Unknown error'
      console.error('Export failed:', raw)
      const short = raw.length > 120 ? raw.slice(0, 120) + '...' : raw
      toast({ title: 'Export failed', description: short, variant: 'destructive' })
    }
    prevExportStatus.current = workflow.finalVideoStatus
  }, [workflow.finalVideoStatus])

  const getAudioMixArgs = () => {
    const mix = videoSeekContext?.audioMix
    if (!mix) return undefined
    const hasChanges = Object.values(mix.mutedTracks).some(Boolean) ||
      Object.values(mix.trackVolumes).some(v => v !== 1) ||
      Object.values(mix.clipMutes).some(Boolean) ||
      Object.values(mix.clipVolumes).some(v => v !== 1)
    if (!hasChanges) return undefined
    return mix
  }

  const handleExport = async (resolution?: ExportResolution) => {
    if (!canExport || isExporting) return
    try {
      await startStitching({ workflowId, audioMix: getAudioMixArgs(), resolution })
    } catch (error) {
      console.error('Export failed:', error)
      const raw = error instanceof Error ? error.message : String(error)
      const short = raw.length > 160 ? raw.slice(0, 160) + '...' : raw
      toast({ title: 'Export failed', description: short, variant: 'destructive' })
    }
  }

  const handleReExport = async (resolution?: ExportResolution) => {
    if (isExporting) return
    try {
      await reExport({ workflowId, audioMix: getAudioMixArgs(), resolution })
    } catch (error) {
      console.error('Re-export failed:', error)
      const raw = error instanceof Error ? error.message : String(error)
      const short = raw.length > 160 ? raw.slice(0, 160) + '...' : raw
      toast({ title: 'Export failed', description: short, variant: 'destructive' })
    }
  }

  const currentAspectRatio: AspectRatio = (aspectRatio as AspectRatio) ?? '16:9'
  const handleAspectRatioChange = useCallback((ratio: AspectRatio) => {
    ps.getState().updateSettings({ aspectRatio: ratio })
  }, [])

  if (timelineOnly) {
    return (
      <div className="flex flex-col h-full w-full bg-neutral-950 text-neutral-200 overflow-hidden">
        <div className="flex-1 min-h-0 overflow-hidden">
          <VideoPreviewPanel
            clips={clips || []}
            workflow={{
              title: workflow.title,
              finalVideoUrl: workflow.finalVideoUrl,
              finalVideoId: workflow.finalVideoId,
              finalVideoStatus: workflow.finalVideoStatus,
              finalVideoError: workflow.finalVideoError,
              finalVideoProgress: workflow.finalVideoProgress,
              audioTracks: workflow.audioTracks,
              textOverlays: workflow.textOverlays,
              timelineMode: (workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode,
            }}
            workflowId={workflowId}
            activeSceneIndex={selectedPlayableIndex}
            onSceneChange={handleSceneChange}
            aspectRatio={aspectRatio}
            onSpatialUpdate={handleSpatialUpdate}
            selectedSpatialId={selectedSpatialId}
            onSpatialSelect={setSelectedSpatialId}
          />
        </div>
        {(clips || []).length > 0 && (
          <div className="shrink-0 border-t border-neutral-800">
            <Timeline
              workflowId={workflowId}
              scenes={clips || []}
              audioTracks={(workflow.audioTracks || []).map(t => {
                if (t.type === 'voiceover' && workflow.projectTtsTimestamps?.length) {
                  return { ...t, transcriptionWords: workflow.projectTtsTimestamps.map(ts => ({ w: ts.word, s: ts.startMs / 1000, e: ts.endMs / 1000 })) }
                }
                const trackWords = audioTrackTranscriptions[t.id]
                if (trackWords?.length) {
                  return { ...t, transcriptionWords: trackWords }
                }
                return t
              })}
              textOverlays={workflow.textOverlays}
              timelineMode={(workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode}
              currentTime={videoSeekContext?.currentTime ?? 0}
              totalDuration={buildTimelineModel((clips || []) as unknown as TimelineClip[], { mode: (workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode }).totalDuration}
              onSeek={(time) => videoSeekContext?.seekTo(time)}
              onScrubStart={() => videoSeekContext?.scrubStart()}
              onScrub={(time) => videoSeekContext?.scrubTo(time)}
              onScrubEnd={() => videoSeekContext?.scrubEnd()}
              currentSceneIndex={selectedClipIndex ?? undefined}
              onSceneChange={setSelectedClipIndex}
              onPlayPause={() => videoSeekContext?.togglePlayPause()}
              onAudioTrackUpdate={handleAudioTrackUpdateWithUndo}
              onTextOverlayUpdate={handleTextOverlayUpdate}
              onDeleteClips={handleDeleteClips}
              onDeleteAudioTracks={handleDeleteAudioTracks}
              onClipReorder={handleClipReorder}
              onClipTrimUpdate={handleClipTrimUpdate}
              onUndo={handleUndo}
              onRedo={handleRedo}
              isExpanded
              isPlaying={videoSeekContext?.isPlaying ?? false}
              onTransitionClick={setTransitionPickerClipId}
              onRemoveTextOverlay={handleRemoveTextOverlay}
              selectedOverlayId={selectedOverlayId}
              onOverlaySelect={setSelectedOverlayId}
              exportDuration={workflow.exportDuration}
              onExportDurationChange={handleExportDurationChange}
              onEnvelopeChange={handleEnvelopeChange}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full relative">
      <EmphasisToast emphasis={uiLayout?.emphasis ?? null} />
      {/* Top header */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/60 bg-card shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {/* Story title pill */}
          <div className="flex items-center">
            <button
              onClick={() => {
                const group = storyGroupRef.current
                if (group) {
                  if (showStoryDrawer) {
                    group.setLayout({ 'story-panel': 0, 'workflow-content': 100 })
                  } else {
                    group.setLayout({ 'story-panel': 35, 'workflow-content': 65 })
                    setExpandedCharacterId(null)
                  }
                }
                setShowStoryDrawer(!showStoryDrawer)
              }}
              className={cn(
                'flex items-center gap-1.5 rounded-full transition-all duration-200',
                showStoryDrawer
                  ? 'bg-surface text-white pl-1 pr-3 py-0.5'
                  : 'hover:scale-110 hover:ring-2 hover:ring-border'
              )}
            >
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center border-[1.5px] border-card shadow-sm shrink-0">
                <FileText size={12} className="text-secondary-foreground" />
              </div>
              {showStoryDrawer && (
                <span className="text-xs font-medium truncate max-w-[240px]">
                  {workflow.title || 'Untitled Video'}
                </span>
              )}
            </button>
          </div>
          {/* Character pills */}
          {workflow.characters && workflow.characters.length > 0 && (
            <div className="flex items-center gap-1">
              {workflow.characters.map((char) => {
                const avatarImage = char.referenceImages?.[0]?.url
                const isExpanded = expandedCharacterId === char.id
                return (
                  <button
                    key={char.id}
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedCharacterId(null)
                        storyGroupRef.current?.setLayout({ 'story-panel': 0, 'workflow-content': 100 })
                      } else {
                        setExpandedCharacterId(char.id)
                        setShowStoryDrawer(false)
                        storyGroupRef.current?.setLayout({ 'story-panel': 35, 'workflow-content': 65 })
                      }
                    }}
                    title={char.name}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full transition-all duration-200',
                      isExpanded
                        ? 'bg-surface text-white pl-1 pr-3 py-0.5'
                        : 'hover:scale-110 hover:ring-2 hover:ring-border'
                    )}
                  >
                    {avatarImage ? (
                      <img
                        src={avatarImage}
                        alt={char.name}
                        className="w-6 h-6 rounded-full object-cover border-[1.5px] border-card shadow-sm"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-secondary-foreground font-medium text-[10px] border-[1.5px] border-card shadow-sm">
                        {char.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isExpanded && (
                      <span className="text-xs font-medium">{char.name}</span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {isAdmin && (
            <a
              href={`/admin/videos/inspect/${workflowId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-red-400/70 hover:text-red-300 hover:bg-red-500/10 transition-colors"
              title="Open admin inspector"
            >
              <Gauge size={12} />
              Inspect
            </a>
          )}
          {/* Aspect ratio picker */}
          <AspectRatioPicker
            value={currentAspectRatio}
            onChange={handleAspectRatioChange}
            clipCount={clips?.length ?? 0}
          />
          {/* Export / Re-export button with settings */}
          {hasClips && (
            <ExportSettingsButton
              canExport={!!canExport}
              hasExport={hasExport}
              isExporting={isExporting}
              exportDisabledReason={exportDisabledReason}
              onExport={handleExport}
              onReExport={handleReExport}
              aspectRatio={currentAspectRatio}
            />
          )}
          <QueryErrorBoundary>
            <ExportsDropdown
              workflowId={workflowId}
              workflow={workflow}
            />
          </QueryErrorBoundary>
        </div>
      </div>

      <PanelGroup orientation="vertical" className="flex-1 overflow-hidden" groupRef={storyGroupRef} defaultLayout={{ 'story-panel': 0, 'workflow-content': 100 }}>
        {/* Drawer panel - shows story OR character detail */}
        <Panel
          id="story-panel"
          minSize="0%"
          maxSize="50%"
        >
          <div className="h-full overflow-y-auto bg-card border-b border-border">
            {expandedCharacterId && workflow.characters ? (() => {
              const char = workflow.characters.find(c => c.id === expandedCharacterId)
              if (!char) return null
              const isRegenerating = regeneratingCharacterId === char.id || char.referenceStatus === 'generating'

              const handleRegenerate = async () => {
                setRegeneratingCharacterId(char.id)
                try {
                  await regenerateCharacterRefs({
                    workflowId,
                    characterId: char.id,
                  })
                } catch (error) {
                  console.error('Failed to regenerate character references:', error)
                } finally {
                  setRegeneratingCharacterId(null)
                }
              }

              return (
                <div className="p-4">
                  <div className="flex gap-5">
                    {char.referenceImages && char.referenceImages.length > 0 ? (
                      <div className="flex gap-3 shrink-0">
                        {char.referenceImages.slice(0, 3).map((img, idx) => (
                          <button
                            key={idx}
                            className="relative group cursor-zoom-in"
                            onClick={() => setLightboxImage({ url: img.url, name: char.name, pose: img.pose })}
                          >
                            <img
                              src={img.url}
                              alt={`${char.name} - ${img.pose}`}
                              className="w-24 h-24 rounded-lg object-cover border border-border shadow-sm group-hover:border-muted-foreground group-hover:shadow-md transition-all"
                            />
                            <div className="absolute bottom-1.5 left-1.5 right-1.5">
                              <span className="text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded capitalize">
                                {img.pose.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors" />
                          </button>
                        ))}
                      </div>
                    ) : isRegenerating ? (
                      <div className="flex gap-3 shrink-0">
                        {[0, 1, 2].map((idx) => (
                          <div
                            key={idx}
                            className="w-24 h-24 rounded-lg bg-muted border border-border flex items-center justify-center shadow-sm"
                          >
                            <Loader2 size={24} className="animate-spin text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          defaultValue={char.name}
                          onBlur={(e) => {
                            const newName = e.target.value.trim()
                            if (newName && newName !== char.name) {
                              updateCharacter({
                                workflowId,
                                characterId: char.id,
                                name: newName,
                              })
                            }
                          }}
                          className="flex-1 font-medium text-foreground bg-card border border-border hover:border-muted-foreground/40 focus:border-muted-foreground focus:outline-none focus:ring-1 focus:ring-border rounded-md px-2.5 py-1.5 transition-all"
                          placeholder="Character name"
                        />
                        <button
                          onClick={handleRegenerate}
                          disabled={isRegenerating}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors shrink-0 ${
                            isRegenerating
                              ? 'bg-muted text-muted-foreground cursor-not-allowed'
                              : 'bg-muted text-secondary-foreground hover:bg-muted/80 hover:text-foreground'
                          }`}
                        >
                          {isRegenerating ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                          <span>{isRegenerating ? '...' : (char.referenceImages?.length ? 'Regen' : 'Gen')}</span>
                        </button>
                      </div>
                      <textarea
                        defaultValue={char.description}
                        onBlur={(e) => {
                          const newDesc = e.target.value.trim()
                          if (newDesc !== char.description) {
                            updateCharacter({
                              workflowId,
                              characterId: char.id,
                              description: newDesc,
                            })
                          }
                        }}
                        rows={3}
                        className="w-full text-sm text-secondary-foreground bg-card border border-border hover:border-muted-foreground/40 focus:border-muted-foreground focus:outline-none focus:ring-1 focus:ring-border rounded-md px-2.5 py-2 resize-none transition-all leading-relaxed"
                        placeholder="Physical description for video generation..."
                      />
                      <div className="flex items-center justify-between pt-1 border-t border-border">
                        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                          <Mic size={12} />
                          Voice
                        </span>
                        <div className="flex items-center gap-2">
                          <VoicePicker
                            currentVoiceId={char.voiceId}
                            onVoiceSelect={(voiceId) => {
                              updateCharacter({
                                workflowId,
                                characterId: char.id,
                                voiceId,
                                voiceProvider: 'dia',
                              })
                            }}
                            onVoiceClone={(file) => handleVoiceClone(file, char.id)}
                            onRemoveVoice={() => {
                              updateCharacter({
                                workflowId,
                                characterId: char.id,
                                clearVoice: true,
                              })
                            }}
                            onPreviewVoice={hasTtsFallback ? async (voiceId) => previewVoice({ voice: voiceId }) : undefined}
                            ttsPreviewUrl={hasTtsFallback ? clips.find(c => c.ttsAudioUrl && c.dialog?.some(d => d.characterId === char.id))?.ttsAudioUrl : undefined}
                            clipsWithTts={hasTtsFallback ? clips.filter(c => c.ttsStatus === 'ready' && c.dialog?.some(d => d.characterId === char.id)).length : 0}
                            klingVoiceStatus={(char as { klingVoiceStatus?: 'none' | 'creating' | 'ready' | 'failed' }).klingVoiceStatus}
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setExpandedCharacterId(null)
                        storyGroupRef.current?.setLayout({ 'story-panel': 0, 'workflow-content': 100 })
                      }}
                      className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-secondary-foreground self-start shrink-0"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )
            })() : !workflow.narrative && uiLayout?.storyPanelHeight ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                <ScrollText className="w-10 h-10 text-muted-foreground/60 mb-4" />
                <h3 className="text-lg font-medium text-secondary-foreground mb-2">Start with your script</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Paste your script, ad copy, or narration text. The AI will break it into scenes and create visual clips for each.
                </p>
                <textarea
                  placeholder="Paste your script here..."
                  className="w-full max-w-lg h-32 p-4 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 resize-none transition-all"
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      onNarrativeChange?.(e.target.value.trim())
                    }
                  }}
                />
              </div>
            ) : (
              <div className="p-4 space-y-4">
                <NarrativeEditor
                  content={workflow.narrative || ''}
                  onChange={onNarrativeChange}
                />
                {workflow.globalContext && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <Palette size={14} />
                      Visual Style
                    </h3>
                    <div className="bg-secondary border border-border rounded-lg p-3">
                      <p className="text-sm text-secondary-foreground">{workflow.globalContext}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Panel>
        <PanelResizeHandle className={`flex items-center justify-center group ${(showStoryDrawer || expandedCharacterId) ? 'h-2 bg-muted hover:bg-muted/80 cursor-row-resize' : 'h-0 overflow-hidden pointer-events-none'}`}>
          {(showStoryDrawer || expandedCharacterId) && <div className="w-8 h-px rounded-full bg-border/60 group-hover:bg-muted-foreground/50 transition-colors" />}
        </PanelResizeHandle>
        <Panel id="workflow-content">
      <div className="h-full overflow-hidden flex">
        <div className="flex-1 min-w-0 h-full overflow-hidden">
              <PanelGroup orientation="vertical" className="h-full" groupRef={videoClipsGroupRef} defaultLayout={{ 'video-preview': 55, 'clips-panel': 45 }}>
                {/* Video Preview + Timeline */}
                <Panel id="video-preview" defaultSize={55} minSize={20} collapsible>
                  <VideoTimelineContainer timelineExpanded={timelineExpanded} onCollapseTimeline={() => setTimelineExpanded(false)}>
                    <div className="flex-1 min-h-0 overflow-hidden">
                      <VideoPreviewPanel
                        clips={clips || []}
                        workflow={{
                          title: workflow.title,
                          finalVideoUrl: workflow.finalVideoUrl,
                          finalVideoId: workflow.finalVideoId,
                          finalVideoStatus: workflow.finalVideoStatus,
                          finalVideoError: workflow.finalVideoError,
                          finalVideoProgress: workflow.finalVideoProgress,
                          audioTracks: workflow.audioTracks,
                          textOverlays: workflow.textOverlays,
                          timelineMode: (workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode,
                        }}
                        workflowId={workflowId}
                        activeSceneIndex={selectedPlayableIndex}
                        onSceneChange={handleSceneChange}
                        aspectRatio={aspectRatio}
                        onSpatialUpdate={handleSpatialUpdate}
                        selectedSpatialId={selectedSpatialId}
                        onSpatialSelect={setSelectedSpatialId}
                      />
                    </div>
                    {(clips || []).length > 0 && (() => {
                      const at = workflow.audioTracks || []
                      const tlHeight = getTimelineHeight({
                        hasVideoAudio: (clips || []).some((s: any) => s.videoStatus === 'completed' && (s.nativeAudioEnabled || (s.transcription?.words && s.transcription.words.length > 0))),
                        musicCount: at.filter((t: any) => t.type === 'music').length,
                        sfxCount: at.filter((t: any) => t.type === 'sfx').length,
                        voiceCount: at.filter((t: any) => t.type === 'voiceover').length,
                        textOverlayCount: (workflow.textOverlays || []).length,
                      })
                      return (
                      <div className={`${timelineExpanded ? 'shrink min-h-0 overflow-auto' : 'shrink-0'}`} style={timelineExpanded ? { maxHeight: tlHeight } : undefined}>
                        <Timeline
                          workflowId={workflowId}
                          scenes={clips || []}
                          audioTracks={(workflow.audioTracks || []).map(t => {
                            if (t.type === 'voiceover' && workflow.projectTtsTimestamps?.length) {
                              return { ...t, transcriptionWords: workflow.projectTtsTimestamps.map(ts => ({ w: ts.word, s: ts.startMs / 1000, e: ts.endMs / 1000 })) }
                            }
                            const trackWords = audioTrackTranscriptions[t.id]
                            if (trackWords?.length) {
                              return { ...t, transcriptionWords: trackWords }
                            }
                            return t
                          })}
                          textOverlays={workflow.textOverlays}
                          timelineMode={(workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode}
                          currentTime={videoSeekContext?.currentTime ?? 0}
                          totalDuration={buildTimelineModel((clips || []) as unknown as TimelineClip[], { mode: (workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode }).totalDuration}
                          onSeek={(time) => videoSeekContext?.seekTo(time)}
                          onScrubStart={() => videoSeekContext?.scrubStart()}
                          onScrub={(time) => videoSeekContext?.scrubTo(time)}
                          onScrubEnd={() => videoSeekContext?.scrubEnd()}
                          currentSceneIndex={selectedClipIndex ?? undefined}
                          onSceneChange={setSelectedClipIndex}
                          onPlayPause={() => videoSeekContext?.togglePlayPause()}
                          onAudioTrackUpdate={handleAudioTrackUpdateWithUndo}
                          onTextOverlayUpdate={handleTextOverlayUpdate}
                          onDeleteClips={handleDeleteClips}
                          onDeleteAudioTracks={handleDeleteAudioTracks}
                          onClipReorder={handleClipReorder}
                          onClipTrimUpdate={handleClipTrimUpdate}
                          onUndo={handleUndo}
                          onRedo={handleRedo}
                          isExpanded={timelineExpanded}
                          onToggleExpand={() => setTimelineExpanded(prev => !prev)}
                          isPlaying={videoSeekContext?.isPlaying ?? false}
                          onTransitionClick={setTransitionPickerClipId}
                          onRemoveTextOverlay={handleRemoveTextOverlay}
                          selectedOverlayId={selectedOverlayId}
                          onOverlaySelect={setSelectedOverlayId}
                          exportDuration={workflow.exportDuration}
                          onExportDurationChange={handleExportDurationChange}
                          onEnvelopeChange={handleEnvelopeChange}
                        />
                      </div>
                      )
                    })()}
                  </VideoTimelineContainer>
                </Panel>
                <PanelResizeHandle className="h-1 bg-muted/60 hover:bg-muted cursor-row-resize flex items-center justify-center group">
                  <div className="w-6 h-px rounded-full bg-border/60 group-hover:bg-muted-foreground/50 transition-colors" />
                </PanelResizeHandle>
                <div className="relative flex items-center justify-center h-4 bg-gradient-to-r from-border/30 via-border/50 to-border/30 hover:via-muted-foreground/20 cursor-pointer group/clips-toggle shrink-0 transition-colors" onClick={() => {
                  const group = videoClipsGroupRef.current
                  if (!group) return
                  if (clipsCollapsed) {
                    group.setLayout({ 'video-preview': 55, 'clips-panel': 45 })
                    setClipsCollapsed(false)
                  } else {
                    group.setLayout({ 'video-preview': 100, 'clips-panel': 0 })
                    setClipsCollapsed(true)
                  }
                }} title={clipsCollapsed ? 'Expand clips' : 'Collapse clips'}>
                  <span className="absolute left-3 text-[9px] font-medium tracking-widest uppercase text-muted-foreground/60 opacity-0 group-hover/clips-toggle:opacity-100 transition-opacity duration-200">{clipsCollapsed ? 'Expand Clips' : 'Collapse Clips'}</span>
                  <ChevronDown size={14} strokeWidth={2} className={`text-muted-foreground/60 group-hover/clips-toggle:text-secondary-foreground transition-transform ${clipsCollapsed ? 'rotate-180' : ''}`} />
                </div>
                {/* Clips panel */}
                <Panel id="clips-panel" defaultSize={45} minSize={5} collapsible>
                    <div className="h-full overflow-y-auto px-3 py-3">
                      {audioFirstClips && !hasAudioTracks && (clips || []).length === 0 ? (
                        <AudioUploadHero onUpload={handleAudioUpload} />
                      ) : audioFirstClips && hasTranscription ? (
                        <ScriptPanel
                          clips={(clips || []) as any}
                          characters={workflow.characters || []}
                          projectTtsStatus={workflow.projectTtsStatus}
                          projectTtsTimestamps={workflow.projectTtsTimestamps as any}
                          workflowId={workflowId}
                          onSelectSegment={handleSelectSegment}
                        />
                      ) : (
                        <ClipsPanel
                          clips={clips || []}
                          onClipClick={handleClipClick}
                          characters={workflow.characters}
                          activeClipIndex={selectedPlayableIndex}
                          aspectRatio={aspectRatio}
                          transitionPickerClipId={transitionPickerClipId}
                          onTransitionPickerChange={setTransitionPickerClipId}
                        />
                      )}
                    </div>
                </Panel>
              </PanelGroup>
        </div>
        <RightSidebar
          workflowId={workflowId}
          messages={messages}
          clips={clips}
          characters={workflow.characters}
          isAdmin={isAdmin}
          selectedClipIndex={selectedClipIndex}
          currentTime={videoSeekContext?.currentTime ?? 0}
          textOverlays={workflow.textOverlays}
          selectedOverlayId={selectedOverlayId}
          onOverlaySelect={setSelectedOverlayId}
          onOverlayUpdate={handleOverlayStyleUpdate}
          transitionPickerClipId={transitionPickerClipId}
          onTransitionPickerChange={setTransitionPickerClipId}
          projectTtsStatus={workflow.projectTtsStatus}
          projectTtsTimestamps={workflow.projectTtsTimestamps as any}
          layoutActiveTab={uiLayout?.activeSidebarTab}
          timelineMode={(workflow as typeof workflow & { timelineMode?: 'ripple' | 'preserve_positions' }).timelineMode}
        />
      </div>
        </Panel>
      </PanelGroup>
      <Lightbox url={lightboxImage?.url ?? null} type="image" onClose={() => setLightboxImage(null)} />
    </div>
  )
}
